/* eslint-disable @typescript-eslint/no-explicit-any */
import { config } from '../config'
import type { GraphData, GraphStatus } from '../types/graph'
import { ensureFreshToken, getUserInfo } from '../config/keycloakClient'
import { notifyAnnotatorBlocked, ANNOTATOR_READONLY_MESSAGE } from './annotatorAccess'
import {
  FEATURE_NOT_ENABLED_CODE,
  FEATURE_NOT_ENABLED_MESSAGE,
  notifyFeatureRefusedByApi,
} from '@/features/featureAccess/features'
import { isAnnotatorRole } from '@/types/users'
import { isFabricEmbedMode, waitForEmbedToken, getEmbedWorkspaceId, signalTokenExpired } from '../lib/fabricEmbedAuth'
import { currentColorScheme } from '../lib/colorScheme'
import type { DocumentRecord, PaginatedDocumentsResponse } from '../types/catalog'
import type { VideoBriefStoryboard } from '@/types/videoBrief'
import type { ConnectorRecord } from '@/types/connector'
import type { EventhouseConnectionSummary, EventhouseReadingsPage } from '@/types/eventhouse'
import type {
  SheetConnector,
  SheetConnectorListResponse,
  CreateSheetConnectorRequest,
  UpdateSheetConnectorRequest,
  CreateWithMirrorResponse,
} from '@/types/sheet-connector'
import type { ConnectedGraphsResponse } from '@/types/connected-graphs'
import { UpdateUserRequest, UpdateUserResponse, type UserRole } from '@/types/users'
import type {
  DocumentVisualElements,
  VisualElement,
  VisualElementSummary,
} from '@/types/visual-elements'
import type { ExtractedTablesResponse } from '@/types/extracted-tables'
import type {
  CreateSharesRequest,
  CreateSharesResponse,
  Share,
  ShareResourceType,
} from '@/types/shares'
import { generateCorrelationId } from '../utils/correlationId'
import { CORRELATION_ID_HEADER } from '../constants/headers'
import { recordApiCall, recordError } from '../utils/bugReportDiagnostics'
import { extractErrorMessage } from './error-extract'
import type {
  UIMessagePart,
  WorkflowProgressPart,
  PendingSharePart,
} from '@intuigence/response-parts'

export interface AgentPersona {
  id: string
  name: string
  role: string
  title: string
  description: string
  image: string
  isOnline: boolean
  systemPrompt: string
  capabilities: string[]
  backendAgent: string
  showInFrontend: boolean
  availableForMeeting: boolean
  status: string
}

export interface AgentPersonasConfigResponse {
  agents: Record<string, AgentPersona>
  config: Record<string, string>
}

import type {
  JobRecord,
  JobRunRecord,
  JobStatusType,
  JobRunStatusType,
} from '@/types/job'

import type {
  Comment,
  CreateCommentRequest,
  UpdateCommentRequest,
  CommentFilters,
} from '@/types/comment'
import type {
  ZoomHostMeetingResponse,
  ZoomLinkAccountRequest,
  StartZoomMeetingRequest,
  GoogleMeetHostMeetingResponse,
  GoogleMeetLinkAccountRequest,
  StartGoogleMeetMeetingRequest,
  MSTeamsHostMeetingResponse,
  MSTeamsLinkAccountRequest,
  StartMSTeamsMeetingRequest,
  MeetingIntegrationsStatusResponse,
  ZoomIntegrationStatusDto,
  GoogleMeetIntegrationStatusDto,
  MSTeamsIntegrationStatusDto,
} from '@/types/meeting-integrations'

/**
 * Deadline applied to every `fetchOrError` call that does not opt out.
 *
 * INFRA-173 / FS-680: the frontend had no request timeout, so a degraded
 * endpoint froze the UI permanently instead of erroring. 45s is deliberately
 * well past any healthy request (the board list is now ~90ms, the slowest
 * ordinary read is a document query at a few seconds) and past Kong's own 60s
 * upstream deadline being useful to wait for — by the time we are here, the
 * gateway is already retrying and the user has been staring at a spinner.
 *
 * Streams and bulk transfers opt out with `timeoutMs: null`; they are the only
 * requests whose healthy duration is genuinely unbounded.
 */
const DEFAULT_REQUEST_TIMEOUT_MS = 45_000

/**
 * Merge a caller's AbortSignal with our timeout signal.
 *
 * `AbortSignal.any` is not in every browser we support, so fall back to
 * forwarding whichever fires first into a fresh controller.
 */
function combineAbortSignals(
  ...signals: (AbortSignal | null | undefined)[]
): AbortSignal | undefined {
  const live = signals.filter((s): s is AbortSignal => !!s)
  if (live.length === 0) return undefined
  if (live.length === 1) return live[0]
  if (typeof AbortSignal.any === 'function') return AbortSignal.any(live)

  const controller = new AbortController()
  for (const signal of live) {
    if (signal.aborted) {
      controller.abort(signal.reason)
      break
    }
    // `{ once: true }` and the shared controller keep this from leaking: the
    // listeners drop as soon as either signal fires, and both signals are
    // request-scoped.
    signal.addEventListener('abort', () => controller.abort(signal.reason), { once: true })
  }
  return controller.signal
}

export interface CreateConnectorServiceClientRequest {
  connector_name: string
  connector_id: string
}

// ==================== DIGITAL COWORKER TYPES ====================

export interface DigitalCoworkerGoogleIntegration {
  is_connected: boolean
  email: string | null
  last_synced_at: string | null
}

export interface DigitalCoworkerRecord {
  id: string
  name: string
  job_title: string
  department: string
  expertise: string | null
  type: 'me' | 'colleague'
  photo_url: string | null
  avatar_photo_url: string | null
  status: 'pending' | 'training' | 'active'
  created_by: string
  created_at: string
  updated_at: string
  last_synced_at: string | null
  google_integration: DigitalCoworkerGoogleIntegration
  indexed_documents_count: number
  interviews_count: number
}

export interface DigitalCoworkersListResponse {
  digital_coworkers: DigitalCoworkerRecord[]
}

export interface DigitalCoworkerSingleResponse {
  digital_coworker: DigitalCoworkerRecord
}

export interface CreateDigitalCoworkerRequest {
  name: string
  job_title: string
  department: string
  expertise?: string
  type: 'me' | 'colleague'
}

export interface CreateConversationResponse {
  conversation_id: string
}

export interface CreateConversationRequest {
  context_id: string
  context_type: 'workspace' | 'document'
}

export interface ChatConversation {
  id: string
  context_id: string
  context_type: string
  created_at: string
  updated_at: string
  first_message: string
  active_agent: string
  /** FS-539: user-set title; when null/absent the UI falls back to first_message. */
  title?: string | null
}

export type MessageRole = 'user' | 'assistant'

/**
 * What a reasoning step opens onto (FS-659). Built by the backend from the
 * turn's actual tool rows: a closed-vocabulary `kind`, a result class, one
 * static summary sentence, and — for a tool that declared its output
 * user-facing — an excerpt. `sources` are already-resolved board documents,
 * the same shape and the same links the answer's Sources list uses.
 *
 * Absent on a legacy (kg-agent / Python) step, which renders as it always has.
 */
export interface ReasoningStepDetail {
  kind:
    | 'retrieving'
    | 'searching'
    | 'reading'
    | 'counting'
    | 'tracing'
    | 'documents'
    | 'historian'
    | 'web'
    | 'other'
  outcome: 'ok' | 'empty' | 'error'
  summary: string
  preview?: string
  sources?: MessageSource[]
}

export interface ReasoningStep {
  step: number
  description: string
  timestamp?: string
  completed?: boolean
  durationMs?: number
  detail?: ReasoningStepDetail
}

export interface ReasoningPath {
  steps: ReasoningStep[]
  startTime: string
  endTime?: string
  totalDuration?: string
  status: 'in_progress' | 'completed' | 'error' | 'stopped'
  // AGNT-556: prose chain-of-thought from the NestJS KG agent.
  // When present, the reasoning is long-form (a Qwen `<think>` block,
  // not discrete steps); MessageBubble routes such messages to the
  // brain-icon Thinking-process accordion instead of the step-by-step
  // ReasoningPath display, which better suits flowing prose.
  text?: string
}

export interface AddFeedbackRequest {
  value?: boolean
  rating?: number  // 1-5 star rating
  message?: string
  screenshot?: string
  userQuestion?: string
  assistantAnswer?: string
}

export interface ChartData {
  data: string // base64 encoded image
  filename: string
  url?: string // fallback URL
  size?: number
  type?: string
  // Chart metadata for display
  time_range?: string // e.g., "11/14 to 12/14"
  days_span?: number // e.g., 30
  chart_type?: string // e.g., "timeseries", "distribution", "deviation"
  title?: string // e.g., "Overall Delta T across Heat_Exchangers"
}

/**
 * Wire shape of a single structured citation, as emitted by BOTH backend
 * paths: the NestJS kg-agent (`KgSourceCollector.snapshot()`) and the
 * Python chat (`response_kwargs["sources"]`). snake_case to match the
 * wire; the chatV2 layer maps this to its camelCase `SourcePart`.
 * ENG-2416 §10: the legacy `MessageBubble` renders this array directly
 * as a frontier-style "Sources" list.
 */
export interface MessageSource {
  type?: 'pid_diagram' | 'document' | 'knowledge_graph' | 'timeseries'
  source_id?: string
  title?: string
  content?: string
  confidence?: number
  url?: string
  // pid_diagram / knowledge_graph
  entity_id?: string
  pid_source?: string
  // document
  doc_id?: string
  doc_name?: string
  sheet_name?: string
  page_number?: number
  // timeseries
  tag_id?: string
}

export interface Message {
  id: string
  conversation_id: string
  role: MessageRole
  content: string
  timestamp: string
  user_message_id?: string
  session_id?: string // Session ID for stream cancellation
  stream_action?: 'append' | 'replace' // How to apply streamed content updates
  error?: string
  feedback?: boolean
  rating?: number // 1-5 star rating
  thinking?: string // Legacy text-based thinking
  reasoning_path?: ReasoningPath // New structured reasoning
  charts?: string[] // Legacy chart URLs
  chart_data?: ChartData[] // New base64 chart data for timeseries mode
  // ENG-2416 §10: structured citations from the SSE `sources` envelope.
  // Rendered as a frontier-style Sources list by MessageBubble. Both
  // backend paths populate this; the legacy UI previously ignored it.
  sources?: MessageSource[]
  parts?: UIMessagePart[]
  workflow_progress?: WorkflowProgressPart
  /** FS-663: a share the agent prepared, awaiting the user's confirmation. */
  pending_share?: PendingSharePart
  agent_used?: string // Track which agent generated this message
  responseDurationMs?: number // Client-measured response time (ms)
  // Correlation ID echoed back from the backend's `x-correlation-id`
  // response header. By AGNT-684 convention the backend uses this value
  // as the Langfuse trace id, so the chat UI can deep-link to
  // /kg-trace/:correlationId for forensics. AGNT-686: now persisted on
  // the message entity and returned on conversation reload.
  correlation_id?: string
}

export interface ArchivedMessageResponse {
  id: string
  role: string
  content: string
  timestamp: string
  agent_used?: string
  chart_data?: ChartData[]
  sources?: Record<string, unknown>[]
  reasoning_path?: ReasoningPath
}

export interface MessageVersionGroup {
  version_number: number
  user_message: ArchivedMessageResponse | null
  assistant_messages: ArchivedMessageResponse[]
}

export interface MessageVersionGroupResponse {
  version_group_id: string
  versions: MessageVersionGroup[]
}

// Structured response types for engineering agents (AICE, MAX, ELLA)
export interface MathEquation {
  latex: string
  description?: string
  is_display: boolean
}

export interface TableData {
  headers: string[]
  rows: string[][]
  caption?: string
}

export interface CalculationStep {
  step_number: number
  title: string
  explanation: string
  equation?: MathEquation
  result?: string
}

export interface EngineeringResponse {
  problem_summary: string
  given_data?: TableData
  steps: CalculationStep[]
  final_answer: string
  final_equation?: MathEquation
  notes?: string
}

interface SaveNodePropertiesRequest {
  nodeId: string
  properties: Record<string, string>
}

interface Property {
  id: string
  target_id: string
  is_node_property: boolean
  key: string
  value: string
}

interface GraphPatch {
  nodes_to_add: string[]
  nodes_to_delete: string[]
  edges_to_add: Record<string, unknown>[]
  edges_to_delete: string[]
  properties_to_upsert: Property[]
  properties_to_delete: string[]
  /**
   * Set when the user is deliberately restoring an OLDER version, which reverts by
   * deleting everything added since. The backend refuses that shape without it,
   * because it is otherwise indistinguishable from a client that lost its state
   * and is about to wipe the drawing.
   */
  allow_bulk_delete?: boolean
}

export interface BoundingBox {
  x: number
  y: number
  x1: number
  y1: number
}

export interface DetectBoundaryResponse {
  detected: boolean
  boundary: {
    x: number
    y: number
    width: number
    height: number
    area: number
    contour_points: number
    method: string
  } | null
}

export interface GroundTruthSnapshotMeta {
  id: string
  document_id: string
  page: number
  graph_id: string
  graph_version: number
  sha256: string
  created_by: string | null
  created_at: string
}

export interface RecordGroundTruthResult {
  skipped: boolean
  reason?: string
  snapshot?: GroundTruthSnapshotMeta
}

export interface LatestGroundTruthResult {
  snapshot: (GroundTruthSnapshotMeta & { payload: Record<string, unknown> }) | null
}

export interface DetectConnectionsResponse {
  connected_symbols: {
    symbol_id: string
    line_id: string
    snap_point: [number, number]
    original_endpoint: [number, number]
  }[]
  dangling_lines: {
    line_id: string
    endpoint_near_bbox: [number, number]
    other_endpoint: [number, number]
  }[]
  associated_tags: {
    id: string
    content: string
    geometry: Record<string, unknown>
  }[]
  candidate_points: {
    x: number
    y: number
    line_id: string
    symbol_id: string | null
  }[]
}

export interface DocumentPosition {
  document_id: string
  x: number
  y: number
  doc_type: string
}

export interface TldrawSnapshot {
  document: Record<string, unknown> | unknown
  session?: Record<string, unknown> | unknown
}

export interface WorkspaceOperation {
  upsert: DocumentPosition[]
  remove: string[]
  tldraw_snapshot?: TldrawSnapshot
}

export interface UpdateWorkspaceRequest {
  name?: string
  operations?: WorkspaceOperation
  preview_image?: string
}

export interface Workspace {
  id: string
  name: string
  tenant_id: string
  user_id: string
  documents: DocumentPosition[]
  created_at: string
  updated_at: string
  preview_image?: string
  tldraw_snapshot?: TldrawSnapshot
}

export interface WorkspacePreviewImage {
  workspace_id: string
  preview_image: string
}

export interface PnidTagInfo {
  tag: string
  document_id: string | null
  document_name: string | null
}

export interface EntityMatchCandidate {
  /** Persisted match row id, present for tenant-scoped (Data Catalog) results
   *  — the run auto-saves them, so this is the target for tenant feedback. */
  id?: string
  pid_tag: string
  pid_document_id?: string | null
  ts_tag: string
  confidence: number
  match_method: string
  reasoning: string
  equipment_type?: string | null
  /** "auto_accept" | "manual_review" | "reject" — drives the Quality badge in the UI. */
  match_quality?: string | null
}

export interface EntityMatchingResponse {
  matches: EntityMatchCandidate[]
  unmatched_pid_tags: string[]
  unmatched_ts_tags: string[]
  execution_time_ms: number
  total_matches: number
  average_confidence: number
}

export interface SavedEntityMatch {
  id: string
  workspace_id: string | null
  tenant_id: string
  pid_tag: string
  pid_document_id?: string | null
  ts_tag: string
  confidence: number
  match_method: string
  reasoning: string
  equipment_type?: string | null
  created_by: string
  created_at: string
  updated_at: string
}

/**
 * One row returned by POST /v1/tenant/entity-matching. Mirrors the shape
 * EntityMatchingRepository.save_tenant_matches emits — the rows are
 * auto-saved server-side (workspace_id=NULL, deduped on tenant_id+pid+ts),
 * so the Data Catalog UI can render them without a save round-trip.
 */
export interface TenantMatchResultRow {
  id: string
  pid_tag: string
  ts_tag: string
  confidence: number
  match_method: string
  reasoning: string
  equipment_type?: string | null
  pid_document_id?: string | null
}

export interface TenantMatchingResponse {
  match_count: number
  pid_tag_count: number
  ts_tag_count: number
  duration_ms: number
  skipped: boolean
  skip_reason: string
  matches: TenantMatchResultRow[]
}

/** Latest accept/reject decision on a tenant-scoped match, keyed by match_id. */
export interface TenantMatchFeedback {
  match_id: string
  action: 'accepted' | 'rejected' | 'modified'
}

// ---- Tag mapping (FS-655) ----
// The review surface over the matches the Match Entity run produces. Served by
// the Python tier under the same /v1/tenant/entity-matching prefix, which the
// NestJS gateway already proxies.

/** Counts behind the Matched / Pending tab badges. */
export interface TagMappingSummary {
  matched_count: number
  /** Null when nothing could produce a count at all. */
  pending_count: number | null
  /**
   * False when `pending_count` is the ~1%-accurate SQL estimate rather than a
   * number a real pass produced. The badge shows it either way — a dash reads as
   * broken — but marks the estimate so it never poses as the exact figure.
   */
  pending_count_exact?: boolean
}

/** One matched counterpart of a source tag. */
export interface MatchedTagRow {
  match_id: string
  tag: string
  confidence: number
  /** `'manual'` renders as "Manual Approval" instead of a percentage. */
  match_method: string
  decision?: 'accepted' | 'rejected' | 'modified' | null
  equipment_type?: string | null
  pid_document_id?: string | null
  /** The timeseries file this tag lives in, so the row can link to it. */
  ts_document_id?: string | null
  ts_document_name?: string | null
  /**
   * `documents.type` of the file above — the Matched table's "Source type".
   *
   * Reads `timeseries` on every row today: the matcher only ever pairs a
   * drawing tag with a historian tag. Carried as data rather than assumed in
   * the client so the column cannot silently lie if that widens.
   */
  source_type?: string | null
}

/**
 * What the tenant's documents say about a matched tag. Evidence, not a match —
 * a document names an instrument in prose rather than claiming two identifiers
 * are the same thing, so there is nothing to accept or reject.
 */
export interface MatchedDocumentRef {
  /** ONE document that names the tag — the table draws one line per entry (review F00). */
  document_id: string
  document_name: string
  /** The spelling this document uses most. */
  mention: string
  passage_count: number
  /**
   * The MEAN confidence over this document's passages, or null when none carried one.
   *
   * This is the extractor's confidence that the passage really named the tag —
   * NOT a claim that two identifiers are the same device, which is what a match
   * asserts and evidence does not.
   *
   * One scale for both kinds of mention: 0.97/0.93 for a tag matched against
   * the registry (exact spelling vs one reduced to the device core), and
   * 0.92/0.80/0.75 for one discovered in the prose. Null only for rows written
   * before the scanner scored registry hits, until their document is re-scanned.
   */
  confidence?: number | null
}

/** A source tag and everything matched to it. */
export interface MatchedTagGroup {
  tag: string
  matches: MatchedTagRow[]
  /** One entry per document that names the tag, best-scored first; absent when none does. */
  documents?: MatchedDocumentRef[] | null
}

/** Page of the Matched tab. `total` counts source tags, not match rows. */
export interface MatchedTagsResponse {
  groups: MatchedTagGroup[]
  total: number
}

/**
 * A tag with no saved match. `tag` is the raw parse content and the identity to
 * connect with; `display_tag` is what to show (they differ when the parse
 * merged a device into surrounding drawing text).
 */
/** A sheet a pending tag sits on; the name travels with its own id. */
export interface PendingSourceRef {
  document_id: string
  document_name: string
}

export interface PendingTagRow {
  tag: string
  display_tag: string
  candidate_tag?: string | null
  /**
   * The timeseries file the candidate tag was read from, so the cell can open
   * it the way Matched opens a mapped tag: the sheet viewer, on that column.
   * Null when there is no candidate, or its file is gone.
   */
  candidate_document_id?: string | null
  candidate_document_name?: string | null
  confidence: number
  source_document_id?: string | null
  source_document_name?: string | null
  /** Every sheet this tag sits on — the row displays the first. */
  source_documents?: PendingSourceRef[]
  /**
   * Where the tag was read from: a drawing, or only a document's prose.
   *
   * Since FS-675 this also decides the row's ACTIONS. A sheet tag needs mapping
   * (Connect); a tag only named in prose — or only ever asked about — needs
   * validating first, because whether it is a real device is still an open
   * question, and Connect cannot express that answer.
   */
  source?: 'sheet' | 'document'
  /**
   * FS-675 — the question that went unanswered, when this row exists because a
   * user asked about a tag nothing could resolve. Null otherwise.
   */
  asked_question?: string | null
  /**
   * `documents.type` of the source file — the Pending tab's "Source type".
   * Null when the row has no source document at all, which is the FS-675 case:
   * a tag that exists only because somebody asked about it has no file to type.
   */
  source_type?: string | null
  /**
   * `confirmed` or `not_a_tag` once a reviewer has decided this tag. A decided
   * row KEEPS ITS PLACE rather than vanishing, so the reviewer can see the
   * decision registered and revisit a mistake; the verdict changes which actions
   * the row offers, not whether it is listed.
   */
  verdict?: 'confirmed' | 'not_a_tag' | null
}

/** One sheet contributing pending tags — a Filter menu entry. */
export interface PendingSourceDocument {
  document_id: string
  document_name: string
  count: number
}

/** Page of the Pending tab. */
export interface PendingTagsResponse {
  rows: PendingTagRow[]
  total: number
  /**
   * Rows still undecided. Separate from `total`, which counts every row and
   * drives pagination — the badge answers "how much is left", and a decided row
   * that stays on screen must stop counting as outstanding work.
   */
  undecided_total?: number
  /** Faceted over the whole pending set, not just this page. */
  documents?: PendingSourceDocument[]
}


export interface TagVerdictResponse {
  tag_core: string
  verdict: 'confirmed' | 'not_a_tag'
  /** False when this replaced an earlier decision on the same identity. */
  created: boolean
}

/** One selectable target in the Connect dialog. */
export interface ConnectCandidate {
  tag: string
  document_id?: string | null
  document_name?: string | null
  description?: string | null
  /** The kind of document the tag lives in — the picker's "Source type" column. */
  source_type?: string | null
  /** The graph document behind a drawing, so its preview can open on the annotated render. */
  graph_id?: string | null
  /** The tag's own node on that drawing, so the preview opens zoomed onto it. */
  node_id?: string | null
  confidence: number
}

/** A sheet a tag appears on. `node_id` deep-links the graph editor to the tag. */
/** One device tag on a sheet, in both forms — see the Python SheetTag. */
export interface SheetTag {
  /** The parse content: the identity, and what the active-tag comparison uses. */
  raw: string
  /** The readable device — what a chip shows. */
  display: string
}

export interface TagSheetRow {
  document_id: string
  document_name: string
  graph_id: string
  node_id?: string | null
  /** Every device tag on that sheet — the Node Details chips. */
  sheet_tags?: SheetTag[]
}

/**
 * One passage in one document that names the tag.
 *
 * Kept separate from TagSheetRow rather than widening it: a passage has no
 * graph or node to deep-link to, and a sheet has no passage to quote, so a
 * shared type would make both halves optional and lose the guarantee that a
 * sheet row can always open the drawing.
 */
export interface TagDocumentRow {
  document_id: string
  document_name: string
  /** The tag as this document spelled it — may differ from the drawing's. */
  mention: string
  /** The surrounding text, so a reviewer can judge without opening the file. */
  context: string
  /** 1.0 when a registry already holds the tag; lower when it was discovered. */
  confidence: number
}

export interface FileMetadata {
  name: string
  description: string
  type: 'pnid' | 'pfd' | 'sld' | 'document' | 'datasheet' | 'timeseries'
  bboxes?: Array<{
    x: number
    y: number
    width: number
    height: number
    type: string
    /**
     * 1-based page for a multi-page PDF. The backend fans out one processing
     * job per annotated page, so omitting this collapses a multi-sheet upload
     * to page 1 — it was missing from this type, which let a caller drop the
     * field with nothing to catch it.
     */
    page?: number
  }> | null
  properties?: {
    multi_page_set_id?: string  // UUID to group files in a multi-page set
    sheet_index?: string  // Order within the set (stored as string in backend: "0", "1", "2", ...)
    /**
     * Opt-in P&ID parsing for mixed-document uploads. When `true`, the
     * BullMQ mixed-document worker runs CASCADE page classification and
     * fans out per-page P&ID parsing jobs. When `false` or absent
     * (the default), every page is treated as regular text. Set by the
     * "Parse P&ID diagrams" Switch in UnifiedUploadModal.
     */
    parse_pnids?: boolean
    [key: string]: any  // Allow other properties
  }
}

// Onboarding question/answer types
export type OnboardingQuestionType =
  | 'text'
  | 'single_select'
  | 'multi_select'
  | 'number'

export interface OnboardingQuestion {
  id: string
  tenant_id: string | null
  question_text: string
  question_type: OnboardingQuestionType
  options: string[] | null
  display_order: number
  is_required: boolean
  is_default: boolean
  is_active: boolean
  created_at: string
  updated_at: string
  created_by: string | null
}

export interface OnboardingAnswer {
  id: string
  question_id: string
  /** wizard step key, or null/absent for an admin-authored question. */
  question_key?: string | null
  answer_value: string | number | string[]
  created_at: string
  updated_at: string
}

export interface OnboardingUserAnswersResponse {
  user_id: string
  tenant_id: string
  answers: OnboardingAnswer[]
}

export interface OnboardingUserWithAnswers {
  user_id: string
  user_email?: string
  user_name?: string
  answers: OnboardingAnswer[]
  submitted_at: string
}

export interface OnboardingAllUsersAnswersResponse {
  tenant_id: string
  users: OnboardingUserWithAnswers[]
}

export interface CreateOnboardingQuestionRequest {
  questionText: string
  questionType: OnboardingQuestionType
  options?: string[]
  displayOrder?: number
  isRequired?: boolean
}

export interface UpdateOnboardingQuestionRequest {
  questionText?: string
  questionType?: OnboardingQuestionType
  options?: string[]
  displayOrder?: number
  isRequired?: boolean
  isActive?: boolean
}

export interface SubmitOnboardingAnswersRequest {
  answers: Array<{
    /** Question uuid — used by the admin-authored dynamic questions (V1 flow). */
    questionId?: string
    /**
     * Stable `question_key` of a code-owned wizard step (`role`, `industry`, …),
     * resolved to the question's uuid server-side. The V2 wizard
     * hardcodes its steps, so it has a key and no uuid to send.
     */
    questionKey?: string
    answerValue: string | number | string[]
  }>
}

/** FS-637 — per-tenant pipeline re-parse policy. */
export type ReparsePolicy = 'auto' | 'always' | 'never'

/** One drawing currently mid-refresh after a pipeline change. */
export type ReprocessingRow = {
  id: string
  original_filename: string
  reprocess_reason: string | null
  reprocessing_started_at: string | null
}

/** FS-637 — everything the Pipeline Reprocessing settings page renders. */
export type ReprocessingStatus = {
  policy: ReparsePolicy
  parse_verdict: 'behind' | 'current'
  behind_since: string | null
  deferred_reason: string | null
  last_activity_at: string | null
  pipeline_revision: string | null
  pipeline_pr: string | null
  pipeline_why: string | null
  drawing_count: number
  refreshing_count: number
  refreshing: ReprocessingRow[]
}

/** Per-tenant egress-consent policy (Trust/Security §9). `ask` pauses each
 *  conversation's first would-be web egress behind an in-chat consent prompt
 *  (see docs/egress-consent-design.md). */
export type EgressPolicyLevel = 'always_allow' | 'ask' | 'always_deny'

/** The egress surfaces a consent verdict can apply to (FS-676). `public_model`
 *  is stored but not yet enforced — it joins with the private-model-fallback
 *  work (INFRA-165). */
export type EgressSurface = 'web_search' | 'public_model'

export type EgressConsentVerdict = 'granted' | 'denied'

/** A conversation's stored consent verdicts, keyed by surface; {} = never asked. */
export type EgressConsentVerdicts = Partial<Record<EgressSurface, EgressConsentVerdict>>

export type EgressConsentWrite = {
  surface: EgressSurface
  verdict: EgressConsentVerdict
}

export type EgressPolicy = {
  tenantId: string
  webSearchPolicy: EgressPolicyLevel
  publicModelPolicy: EgressPolicyLevel
  /** Absent on a tenant that never saved a policy (the API returns defaults
   *  built in-memory, with no row and therefore no timestamps). */
  createdAt?: string
  updatedAt?: string
}

export type EgressPolicyPatch = {
  webSearchPolicy?: EgressPolicyLevel
  publicModelPolicy?: EgressPolicyLevel
}

export interface TenantAgent {
  id: string
  name: string
  role: string
  title: string
  description: string
  image: string
  isProvisioned: boolean
  isEnabled: boolean
  isOnline: boolean
  availableForMeeting: boolean
  alwaysOn: boolean
}

export interface TenantAgentsResponse {
  agents: TenantAgent[]
  config: {
    version: string
    defaultAgent: string
  }
}

/** Per-user custom display name for an agent (AGNT-5). */
export interface UserAgentNameEntry {
  agentId: string
  customName: string
}

export interface TenantProvisioningSummary {
  tenantId: string
  /** Keycloak-unique slug (`organizations.name`). */
  tenantName: string
  /**
   * Human-readable label entered at org creation. Null on legacy rows; the UI
   * shows it as the heading when present and falls back to `tenantName`.
   */
  displayName: string | null
  provisionedCount: number
  totalAgents: number
  provisionedAgents: string[]
  enabledAgents: string[]
}

export interface DocumentQuery {
  type: string | undefined
  doc_ids?: string[]
  /**
   * Requested page size. The legacy /v1/document/query endpoint honors this
   * (clamped server-side); without it the server defaults to 50, so requesting
   * a chunk larger than 50 must send a matching page_size or be re-truncated.
   */
  page_size?: number
  /**
   * FS-511: when true, each returned `graph` document is enriched with its
   * resolved `pnid_source` inline (server-side join), so the board needs no
   * follow-up hydration fetch.
   */
  include_pnid_source?: boolean
}

export interface SearchHighlight {
  x0: number
  y0: number
  x1: number
  y1: number
}

export interface MatchHighlight {
  boxes: SearchHighlight[]
  /** Surrounding text for this specific match. Computed at SEARCH time from the
   *  stored word positions, so it needs no re-index — but it is absent when the
   *  Python tier predates FS-657 (version skew during a rollout), which is why
   *  callers fall back to the page-level SearchResult.text. */
  snippet?: string
}

export interface PageHighlights {
  page_width: number
  page_height: number
  boxes: SearchHighlight[]
  matches: MatchHighlight[]
  match_count: number
}

export interface SearchResult {
  doc_id: string
  page: number
  score: number
  text: string
  highlights?: SearchHighlight[]
  page_highlights?: PageHighlights
}

export interface SearchResponse {
  results: SearchResult[]
}

export interface WordPosition {
  text: string
  x0: number
  y0: number
  x1: number
  y1: number
}

export interface PageWordPositions {
  page: number
  page_width: number
  page_height: number
  words: WordPosition[]
}

export interface QueryResult {
  columns: string[]
  rows: (string | number | boolean | null)[][]
  row_count: number
}

export interface SheetInfo {
  name: string
  table_name: string
  row_count: number
  columns: string[]
}

/** First rows and columns of a sheet, for a board card's thumbnail (GET v1/document/:id/preview-head). */
export interface SheetPreviewHead {
  sheet: string
  columns: string[]
  rows: unknown[][]
  /** Columns the sheet has in all; more than `columns.length` when the head was cut. */
  total_columns: number
}

export interface SheetsResponse {
  document_id: string
  sheets: SheetInfo[]
}

// Connector interfaces
export interface ConnectorHeartbeatRequest {
  config_timestamp: string
}

export interface ConnectorHeartbeatResponse {
  interval_connection: number
  active: boolean
  config_changed: boolean
  config?: Record<string, unknown> | null
  config_timestamp?: string | null
}

export interface ConnectorRequest {
  connector: ConnectorRecord
}

export interface ConnectorIdResponse {
  connector: string
}

export interface ConnectorUpdateRequest {
  name?: string | null
  description?: string | null
  interval_connection?: number | null
  config?: Record<string, unknown> | null
}

export interface ConnectorUpdateResponse {
  updated: boolean
  connector: ConnectorRecord
}

export interface ConnectorListResponse {
  connectors: ConnectorRecord[]
}

export interface ConnectorResponse {
  connector: ConnectorRecord
}

export interface ConnectorActivateRequest {
  activate: boolean
}

export interface ConnectorDiscoverRequest {
  database?: string
  plant_elements?: string[]
  af_templates?: string[]
  af_categories?: string[]
}

export interface ConnectorDiscoverResponse {
  requested: boolean
}
// Finished connector interfaces

// Job / Job Run interfaces

export interface Info {
  count: number
  pages: number
}

export interface ListResponse<T> {
  results: T[]
  info: Info
}

export interface JobRequest {
  job: JobRecord
}

export interface JobUpdateStatusRequest {
  status: JobStatusType
}

export interface JobResponse {
  job: JobRecord
}

export interface JobIdResponse {
  job_id: string
}

export interface JobUpdateResponse {
  updated: boolean
  job: JobRecord
}

export interface JobRunRequest {
  job_run: JobRunRecord
}

export interface JobRunUpdateRequest {
  status: JobRunStatusType
  result?: Record<string, unknown>
}

export interface JobRunResponse {
  job_run: JobRunRecord
}

export interface JobRunIdResponse {
  job_run_id: string
}
// Interview Types
export interface Expert {
  name: string
  email: string
  role?: string
}

export interface Interview {
  id: string
  topic: string
  questions: string[]
  digital_coworker_id?: string
  interview_start_date: string
  interview_end_date: string
  experts: Expert[]
  status: string
  meeting_link?: string
  event_url?: string
  meeting_transcript?: unknown
  meeting_summary?: string
  meeting_action_items?: string[]
  created_by: string
  updated_by: string
  created_at: string
  updated_at: string
}

export interface InterviewCreate {
  topic: string
  questions: string[]
  digital_coworker_id?: string
  interview_start_date: string
  interview_end_date: string
  experts: Expert[]
  webapp_url: string
}

// Interview Document types
export interface InterviewDocumentMetadata {
  name: string
  description?: string
}

export interface DocumentUploader {
  name?: string
  email?: string
  picture?: string
}

export interface InterviewDocument {
  id: string
  interview_id: string
  document_name: string
  document_type?: string
  file_size: number
  uploaded_date: string
  updated_date: string
  uploader: DocumentUploader
}

// Meeting Bot types
export interface MeetingStartRequest {
  meeting_url: string
  webapp_url: string
  agent_id?: string | null
  digital_coworker_id?: string | null
  workspace_id?: string | null
  translator_mode?: boolean
  // "Use Entire Data Catalog" for the whole call: the bot's retrieval tools search the
  // tenant's whole data catalog instead of the board attached to the meeting. Persisted on
  // the meeting token. Always sent (never omitted) — see the chat toggle in api.ts:
  // an absent field is indistinguishable from a dropped toggle in the server logs.
  entire_data_catalog?: boolean
}

export interface MeetingStartDirectRequest {
  agent_id?: string | null
  digital_coworker_id?: string | null
  workspace_id?: string | null
  translator_mode?: boolean
  // See MeetingStartRequest.entire_data_catalog.
  entire_data_catalog?: boolean
}

export interface MeetingStartDirectResponse {
  meeting_token: string
}

// Finished Job / Job Run interfaces

// Admin Interaction Data Types
export interface DatabaseStats {
  total_conversations: number
  total_messages: number
  user_messages: number
  assistant_messages: number
  messages_with_positive_feedback: number
  messages_with_negative_feedback: number
  unique_users: number
  conversations_last_7_days: number
  conversations_last_30_days: number
  active_agents_breakdown: Record<string, number>
}

export interface AdminReasoningStep {
  step: number
  description: string
  timestamp?: string | null
  completed: boolean
}

export interface AdminReasoningPath {
  steps: AdminReasoningStep[]
  start_time: string
  end_time?: string | null
  total_duration?: string | null
  status: 'in_progress' | 'completed' | 'error'
}

export interface InteractionMessage {
  id: string
  role: string
  content: string
  timestamp: string
  feedback_value?: boolean | null
  feedback_message: string
  agent_used?: string | null
  context?: string | null // Document context used for response
  has_chart_data: boolean // Whether chart data is attached
  reasoning_path?: AdminReasoningPath | null // AI reasoning steps and thought process
  feedback_reporter_id?: string | null // User ID who submitted feedback
  feedback_reporter_name?: string | null // Display name of feedback reporter
  feedback_reporter_email?: string | null // Email of feedback reporter
}

export interface ConversationSummary {
  conversation_id: string
  user_id: string
  context_type: string
  context_id: string // Workspace/document ID
  context_name?: string | null // Workspace/board name for better context
  created_at: string
  updated_at: string
  message_count: number
  has_feedback: boolean
  active_agent?: string | null
  messages: InteractionMessage[]
}

export interface FinetuningDataPoint {
  conversation_id: string
  user_message: string
  assistant_response: string
  feedback_value?: boolean | null
  feedback_message: string
  agent_used?: string | null
  timestamp: string
  context?: string | null // Document context used for response
  reasoning_path?: AdminReasoningPath | null // AI reasoning steps
  context_name?: string | null // Workspace/board name
  context_type?: string | null // Type of context (workspace/document)
  feedback_reporter_id?: string | null // User ID who submitted feedback
  feedback_reporter_name?: string | null // Display name of feedback reporter
  feedback_reporter_email?: string | null // Email of feedback reporter
}

export interface OperationalContextDetail {
  id: string
  user_id: string
  session_id: string
  workspace_id?: string | null
  question: string
  question_category: string
  key_findings: string[]
  equipment_mentioned: string[]
  data_sources_used: string[]
  recommendations_made: string[]
  timestamp: string
}

export interface UserPreferenceDetail {
  user_id: string
  preferred_detail_level: string
  preferred_response_format: string
  common_equipment: string[]
  frequent_question_types: string[]
  last_active: string
  created_at: string
}

export interface ContextMemoryStats {
  total_contexts: number
  unique_users: number
  contexts_last_7_days: number
  contexts_last_30_days: number
  question_categories: Record<string, number>
  equipment_mentioned_count: number
  recent_contexts: OperationalContextDetail[]
  user_preferences: UserPreferenceDetail[]
}

export interface AdminDataResponse {
  database_stats: DatabaseStats
  context_memory_stats?: ContextMemoryStats | null
  recent_conversations: ConversationSummary[]
  finetuning_data: FinetuningDataPoint[]
}

// Graph data is sensitive — the backend encrypts GET /v1/graph/:id/data with AES-256-GCM
// so it is not readable in the browser network tab. Decrypted here transparently using
// the browser-native Web Crypto API before the data reaches the rest of the app.
// Note: this is obfuscation, not true security. A determined user can still extract the
// key from the JS bundle. True security requires server-side rendering so raw data never
// reaches the browser. This raises the bar enough for our current threat model.

interface EncryptedGraphResponse {
  iv: string
  data: string
  tag: string
}

function isEncryptedGraphResponse(body: unknown): body is EncryptedGraphResponse {
  return (
    typeof body === 'object' &&
    body !== null &&
    'iv' in body &&
    'data' in body &&
    'tag' in body &&
    typeof (body as any).iv === 'string' &&
    typeof (body as any).data === 'string' &&
    typeof (body as any).tag === 'string'
  )
}

// Lazily derived and cached — SHA-256("intuigenceai") → 32-byte key, matching the backend.
let graphCryptoKey: Promise<CryptoKey> | null = null

function getGraphCryptoKey(): Promise<CryptoKey> {
  if (!window.crypto?.subtle) {
    return Promise.reject(new Error('Web Crypto API unavailable — app must be served over HTTPS or localhost'))
  }
  if (!graphCryptoKey) {
    graphCryptoKey = window.crypto.subtle
      .digest('SHA-256', new TextEncoder().encode('intuigenceai'))
      .then((keyBytes) => window.crypto.subtle.importKey('raw', keyBytes, 'AES-GCM', false, ['decrypt']))
  }
  return graphCryptoKey
}

async function decryptGraphResponse(envelope: EncryptedGraphResponse): Promise<unknown> {
  try {
    const key = await getGraphCryptoKey()
    const iv = Uint8Array.from(atob(envelope.iv), (c) => c.charCodeAt(0))
    const ciphertext = Uint8Array.from(atob(envelope.data), (c) => c.charCodeAt(0))
    const tag = Uint8Array.from(atob(envelope.tag), (c) => c.charCodeAt(0))
    // AES-GCM decryption requires ciphertext + auth tag concatenated
    const combined = new Uint8Array(ciphertext.length + tag.length)
    combined.set(ciphertext)
    combined.set(tag, ciphertext.length)
    const decrypted = await window.crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, combined)
    return JSON.parse(new TextDecoder().decode(decrypted))
  } catch (error) {
    console.error('Failed to decrypt graph response:', error)
    throw new Error('Failed to decrypt graph data. The data may be corrupted or the encryption key may have changed.')
  }
}

export class ApiService {
  private baseUrl: string

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl
  }

  getUrl(
    path: string,
    params?: Record<string, string | number | boolean | undefined>
  ): string {
    // Ensure base URL doesn't end with slash and path doesn't start with slash
    const cleanBaseUrl = this.baseUrl.replace(/\/+$/, '')
    const cleanPath = path.replace(/^\/+/, '')
    const url = new URL(`${cleanBaseUrl}/${cleanPath}`, window.location.origin)
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          url.searchParams.append(key, String(value))
        }
      })
    }
    return url.toString()
  }

  async createConnectorServiceClient(
    request: CreateConnectorServiceClientRequest
  ) {
    const response = await this.fetchOrError(
      this.getUrl(`v1/auth/v1/connector_service_client`),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
      }
    )
    return response.json()
  }

  async uploadFile(
    file: File,
    metadata: FileMetadata,
    signal?: AbortSignal,
    options?: { autoRename?: boolean; replaceDocumentId?: string; uploadStartedAt?: string; docId?: string }
  ) {
    const formData = new FormData()
    formData.append('file', file)
    // v2/files/upload expects 'filename' and 'fileType' fields
    formData.append('filename', metadata.name || file.name)
    if (metadata.type) {
      formData.append('fileType', metadata.type)
    }
    if (metadata.description) {
      formData.append('description', metadata.description)
    }

    // Build properties object: merge bboxes with any extra metadata.properties
    // (e.g. multi_page_set_id, sheet_index, sheet_count for multi-page P&ID uploads)
    const propertiesObj: Record<string, unknown> = {}

    if (metadata.bboxes && metadata.bboxes.length > 0) {
      propertiesObj.bboxes = metadata.bboxes
    }

    if (metadata.properties) {
      Object.assign(propertiesObj, metadata.properties)
    }

    if (Object.keys(propertiesObj).length > 0) {
      formData.append('properties', JSON.stringify(propertiesObj))
    }

    // Preserve deduplication parameters for backward compatibility
    if (options?.autoRename) {
      formData.append('auto_rename', 'true')
    }
    if (options?.replaceDocumentId) {
      formData.append('replace_document_id', options.replaceDocumentId)
    }

    // Send upload start time so server sorts by when user initiated upload, not when it completed
    formData.append('upload_started_at', options?.uploadStartedAt || new Date().toISOString())

    // FS-499: when this upload claims a pre-registered pending row, send the
    // docId so the backend updates the existing row instead of creating new.
    if (options?.docId) {
      formData.append('doc_id', options.docId)
    }

    const response = await this.fetchOrError(
      this.getUrl(`v2/files/upload`),
      {
        method: 'POST',
        body: formData,
        signal,
      },
      // A bulk transfer: its healthy duration scales with file size and the
      // user's link, so a request deadline would abort good uploads.
      { timeoutMs: null }
    )

    return response.json()
  }

  async cancelBatch(batchId: string): Promise<{ cancelled: number }> {
    const response = await this.fetchOrError(
      this.getUrl(`v2/files/batch/${batchId}/cancel`),
      { method: 'POST' }
    )
    return response.json()
  }

  /**
   * FS-499: pre-register a batch of pending file_uploads rows. The returned
   * docIds back the catalog's "uploading" rows, so refresh / tab close
   * doesn't drop queued work — the rows persist in Postgres as
   * upload_status='pending' until the upload endpoint claims them.
   */
  async registerPendingFiles(
    batchId: string,
    files: Array<{
      filename: string
      fileType: string
      fileSize: number
      properties?: Record<string, unknown>
      autoRename?: boolean
      replaceDocumentId?: string
    }>
  ): Promise<{
    batchId: string
    files: Array<{ docId: string; filename: string; fileType: string }>
  }> {
    const response = await this.fetchOrError(this.getUrl(`v2/files/register-pending`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ batchId, files }),
    })
    return response.json()
  }

  /**
   * FS-499: soft-delete unclaimed pending rows from a batch. Idempotent —
   * already-claimed rows are ignored. Pass `docIds` to scope the discard
   * to a subset; omit it to discard everything still pending in the batch.
   */
  async discardPendingFiles(
    batchId: string,
    docIds?: string[]
  ): Promise<{ batchId: string; discardedCount: number }> {
    const response = await this.fetchOrError(this.getUrl(`v2/files/discard-pending`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ batchId, docIds }),
    })
    return response.json()
  }

  async analyzeLines(
    documentId: string
  ): Promise<{ lines: Array<{ line_number: string; source: string; destination: string }> }> {
    const response = await this.fetchOrError(
      this.getUrl(`v2/documents/${documentId}/lines/analyze`),
      { method: 'POST' }
    )
    return response.json()
  }

  async analyzeFlows(
    documentId: string
  ): Promise<{
    image_pnid_doc_id: string | null
    image_page: number | null
    flows: Array<{
      connection_id: string
      coords: Array<[number, number]>
      upstream_point: [number, number] | null
      downstream_point: [number, number] | null
      seed_source: string | null
      is_forked: boolean
      line_numbers: string[]
      note: string | null
    }>
  }> {
    const response = await this.fetchOrError(
      this.getUrl(`v2/documents/${documentId}/lines/flows`),
      { method: 'POST' }
    )
    return response.json()
  }

  async linkTusUpload(
    tusUploadId: string,
    filename: string,
    fileType: string,
    uploadStartedAt?: string
  ): Promise<{ fileId: string }> {
    let metadataStr = `filename ${btoa(filename)},filetype ${btoa(fileType)}`
    if (uploadStartedAt) {
      metadataStr += `,uploadStartedAt ${btoa(uploadStartedAt)}`
    }
    const response = await this.fetchOrError(
      this.getUrl(`v2/files/tus/${tusUploadId}/link`),
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Upload-Metadata': metadataStr,
        },
      }
    )
    return response.json()
  }

  async completeTusUpload(tusUploadId: string, fileId: string): Promise<unknown> {
    const response = await this.fetchOrError(
      this.getUrl(`v2/files/tus/${tusUploadId}/complete`),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileId }),
      }
    )
    return response.json()
  }

  async listDocs(
    sortBy?: string,
    sortDirection?: 'asc' | 'desc',
    signal?: AbortSignal
  ): Promise<DocumentRecord[]> {
    const params: Record<string, string | number | boolean> = {}
    if (sortBy) params.sort_by = sortBy
    if (sortDirection) params.sort_direction = sortDirection

    const response = await this.fetchOrError(
      this.getUrl(`v1/document/list`, params),
      { signal }
    )
    return response.json()
  }

  async listDocsPaginated(
    params: {
      page_number?: number
      page_size?: number
      sort_by?: string
      sort_direction?: 'asc' | 'desc'
      search?: string
      type?: string
      user_id?: string
      is_set?: boolean
      fabric_item_ids?: string[]
      /** ISO timestamp — only return docs created on/after this instant. */
      created_after?: string
      /** 'success' | 'failed' | 'processing' — display-status bucket. */
      status_bucket?: string
    },
    signal?: AbortSignal
  ): Promise<PaginatedDocumentsResponse> {
    const queryParams: Record<string, string | number> = {}
    if (params.page_number != null) queryParams.page_number = params.page_number
    if (params.page_size != null) queryParams.page_size = params.page_size
    if (params.sort_by) queryParams.sort_by = params.sort_by
    if (params.sort_direction) queryParams.sort_direction = params.sort_direction
    if (params.search) queryParams.search = params.search
    if (params.type) queryParams.type = params.type
    if (params.user_id) queryParams.user_id = params.user_id
    if (params.is_set) queryParams.is_set = 'true'
    if (params.fabric_item_ids?.length) queryParams.fabric_item_ids = params.fabric_item_ids.join(',')
    if (params.created_after) queryParams.created_after = params.created_after
    if (params.status_bucket) queryParams.status_bucket = params.status_bucket

    const response = await this.fetchOrError(
      this.getUrl('v1/documents', queryParams),
      { signal }
    )
    return response.json()
  }

  async queryDocs(
    query: DocumentQuery,
    options?: { signal?: AbortSignal }
  ): Promise<DocumentRecord[]> {
    const response = await this.fetchOrError(this.getUrl(`v1/document/query`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(query),
      signal: options?.signal,
    })
    return response.json()
  }

  async getDocument(
    docId: string,
    options?: { signal?: AbortSignal }
  ): Promise<DocumentRecord> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/document/${docId}`),
      { signal: options?.signal }
    )
    return response.json()
  }

  async downloadDocument(docId: string): Promise<Blob> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/document/${docId}/download`),
      undefined,
      // A whole document body — size-bound, not latency-bound.
      { timeoutMs: null }
    )
    return response.blob()
  }

  /**
   * The rendered "Explain as video" mp4. Fetched as a blob because a
   * `<video src>` cannot carry the bearer token; the server honours Range for
   * callers that can.
   */
  async getVideoBriefMedia(docId: string, signal?: AbortSignal): Promise<Blob> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/video-briefs/${docId}/media`),
      signal ? { signal } : undefined,
      { timeoutMs: null }
    )
    return response.blob()
  }

  /**
   * "Explain as video" for one file or every file on a board. The `video` row
   * exists as soon as this returns (status `processing`); the document status
   * stream reports when it is playable.
   */
  async createVideoBrief(input: {
    documentId?: string
    workspaceId?: string
  }): Promise<{ document_id: string; name: string; source_document_ids: string[] }> {
    const response = await this.fetchOrError(this.getUrl('v1/video-briefs'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    })
    return response.json()
  }

  async getVideoBriefStoryboard(docId: string): Promise<VideoBriefStoryboard> {
    const response = await this.fetchOrError(this.getUrl(`v1/video-briefs/${docId}/storyboard`))
    return response.json()
  }

  async deleteDocuments(docIds: string[]): Promise<void> {
    await this.fetchOrError(this.getUrl(`v1/document/delete`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ doc_ids: docIds }),
    })
  }

  async renameDocument(docId: string, name: string): Promise<DocumentRecord> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/document/${docId}/rename`),
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      }
    )
    return response.json()
  }

  async updateDocumentMetadata(
    docId: string,
    toUpdate: Record<string, string>,
    keysToDelete?: string[]
  ): Promise<{ success: boolean }> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/documents/${docId}/metadata`),
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to_update: toUpdate,
          ...(keysToDelete?.length ? { keys_to_delete: keysToDelete } : {}),
        }),
      }
    )
    return response.json()
  }

  async checkDuplicateDocument(
    name: string,
    signal?: AbortSignal,
    documentType?: string
  ): Promise<{ exists: boolean; existing_document: DocumentRecord | null }> {
    const body: { name: string; type?: string } = { name }
    if (documentType != null && documentType !== '') {
      body.type = documentType
    }
    const response = await this.fetchOrError(
      this.getUrl(`v1/document/check-duplicate`),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal,
      }
    )
    return response.json()
  }

  // FS-487: bulk variant. Sends ONE round trip for N filenames instead of
  // fanning out to N parallel calls. Returns one result row per input item.
  // Backend skips Keycloak/DTO enrichment on this path; callers only get
  // `{ name, type?, exists, existing_document_id }`.
  async checkDuplicateDocumentsBulk(
    items: Array<{ name: string; type?: string }>,
    signal?: AbortSignal,
  ): Promise<{
    results: Array<{
      name: string
      type?: string
      exists: boolean
      existing_document_id: string | null
    }>
  }> {
    const response = await this.fetchOrError(
      this.getUrl('v1/document/check-duplicates'),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
        signal,
      }
    )
    return response.json()
  }

  async suggestDocumentName(name: string): Promise<{ suggested_name: string }> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/document/suggest-name`),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      }
    )
    return response.json()
  }

  /**
   * Retry processing for a failed document/file
   * Works with both legacy documents (v1) and unified uploads (v2)
   */
  async retryDocumentProcessing(docId: string): Promise<void> {
    // Try the new unified upload endpoint first (v2/files)
    try {
      await this.fetchOrError(this.getUrl(`v2/files/${docId}/retry`), {
        method: 'POST',
      })
      return
    } catch {
      // Fall back to legacy document endpoint if v2 fails
      await this.fetchOrError(this.getUrl(`v1/document/${docId}/retry`), {
        method: 'POST',
      })
    }
  }

  /**
   * Attach browser-rendered page rasters to an uploaded SLD and mint one blank
   * annotation graph per page.
   *
   * SLDs have no parser, so nothing server-side produces these — see
   * `lib/pdfRasterize.ts` for why the rendering happens in the browser. Pages
   * must be supplied in order: index 0 is page 1.
   */
  async registerSldPages(
    docId: string,
    pages: Blob[],
    options?: { signal?: AbortSignal }
  ): Promise<{ pages: Array<{ page: number; graphId: string; imagePath: string }> }> {
    const form = new FormData()
    pages.forEach((blob, i) => form.append('pages', blob, `page_${i + 1}.png`))
    const response = await this.fetchOrError(
      this.getUrl(`v1/sld/${docId}/pages`),
      { method: 'POST', body: form, signal: options?.signal },
      // Rasters are large and uploaded together — size-bound, not latency-bound.
      { timeoutMs: null }
    )
    return response.json()
  }

  async downloadPnidImage(
    pnidId: string,
    options?: { signal?: AbortSignal; type?: 'original' | 'processed'; page?: number }
  ): Promise<Blob> {
    const params = new URLSearchParams()
    if (options?.type) params.set('type', options.type)
    if (options?.page != null) params.set('page', String(options.page))
    const qs = params.toString()
    const url = this.getUrl(`v1/pnid/${pnidId}/image${qs ? `?${qs}` : ''}`)
    // A full-resolution P&ID raster — size-bound, not latency-bound.
    const response = await this.fetchOrError(url, { signal: options?.signal }, { timeoutMs: null })
    return response.blob()
  }

  async getGraphData(
    graphId: string,
    options?: { version?: number; signal?: AbortSignal }
  ): Promise<GraphData> {
    const response = await this.fetchOrError(
      this.getUrl(
        `v1/graph/${graphId}/data`,
        options?.version ? { version: options.version } : undefined
      ),
      { signal: options?.signal }
    )
    const body = await response.json()
    if (isEncryptedGraphResponse(body)) {
      return decryptGraphResponse(body) as Promise<GraphData>
    }
    return body as GraphData
  }

  /**
   * FS-621 — graph readiness for the editor's poll-while-processing loop.
   * Unlike getGraphData (which 200s with empty data for a not-yet-created
   * graph), this distinguishes ready | pending | failed | not_found.
   */
  async getGraphStatus(
    graphId: string,
    options?: { signal?: AbortSignal }
  ): Promise<GraphStatus> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/graph/${graphId}/status`),
      { signal: options?.signal }
    )
    return (await response.json()) as GraphStatus
  }

  async saveNodeProperties(
    graphId: string,
    request: SaveNodePropertiesRequest
  ): Promise<void> {
    const patch = {
      nodes_to_add: [],
      nodes_to_delete: [],
      edges_to_add: [],
      edges_to_delete: [],
      properties_to_upsert: Object.entries(request.properties).map(
        ([key, value]) => ({
          id: crypto.randomUUID(),
          target_id: request.nodeId,
          is_node_property: true,
          key,
          value,
        })
      ),
      properties_to_delete: [],
    }

    await this.fetchOrError(this.getUrl(`v1/graph/${graphId}/data`), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    })
  }

  async deleteNode(graphId: string, nodeId: string): Promise<void> {
    const patch = {
      nodes_to_add: [],
      nodes_to_delete: [nodeId],
      edges_to_add: [],
      edges_to_delete: [],
      properties_to_upsert: [],
      properties_to_delete: [],
    }

    await this.fetchOrError(this.getUrl(`v1/graph/${graphId}/data`), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    })
  }

  async updateGraph(graphId: string, patch: GraphPatch): Promise<void> {
    await this.fetchOrError(this.getUrl(`v1/graph/${graphId}/data`), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    })
  }

  /**
   * Publish the current HITL corrections on a P&ID graph to the git overlay +
   * enqueue a KG regen (auto-approve-on-save, git-model B). Called after a
   * successful graph save; a no-op (skipped server-side) for non-P&ID graphs.
   */
  async approveHitlCorrections(graphId: string): Promise<void> {
    await this.fetchOrError(this.getUrl(`v1/graph/${graphId}/hitl/approve`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    })
  }

  /**
   * Freeze the editor's GoldDrawing export as the stored ground truth for the
   * drawing page this graph labels. The server keeps every version and refuses
   * to update or delete one, so a re-parse can replace the graph without
   * touching the answer key. Called after a successful human save.
   */
  async recordGroundTruthSnapshot(graphId: string, payload: object): Promise<RecordGroundTruthResult> {
    const response = await this.fetchOrError(this.getUrl(`v1/graph/${graphId}/ground-truth`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    return response.json()
  }

  /** The newest stored ground truth for the page this graph labels, or null if none was stored. */
  async getLatestGroundTruthSnapshot(graphId: string): Promise<LatestGroundTruthResult> {
    const response = await this.fetchOrError(this.getUrl(`v1/graph/${graphId}/ground-truth/latest`))
    return response.json()
  }

  async detectManualSymbolConnections(
    graphId: string,
    bbox: { x: number; y: number; width: number; height: number },
    tolerancePx: number = 20,
  ): Promise<DetectConnectionsResponse> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/pnid/graph/${graphId}/detect-connections`),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bbox, tolerance_px: tolerancePx }),
      },
    )
    return response.json()
  }

  async detectEquipmentBoundary(
    graphId: string,
    clickX: number,
    clickY: number,
    morphKernelSize: number = 11,
    useConvexHull: boolean = true,
  ): Promise<DetectBoundaryResponse> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/pnid/graph/${graphId}/detect-boundary`),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          click_x: clickX,
          click_y: clickY,
          morph_kernel_size: morphKernelSize,
          use_convex_hull: useConvexHull,
        }),
      },
    )
    return response.json()
  }

  async getConversation(conversationId: string): Promise<ChatConversation> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/conversations/${conversationId}`)
    )
    const { conversation } = await response.json()
    if (!conversation) throw new Error('Conversation not found')
    return conversation
  }

  async getConversationsByContext(
    contextId: string
  ): Promise<ChatConversation[]> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/conversations/context/${contextId}`)
    )
    return (await response.json()).conversations
  }

  async deleteConversation(
    conversationId: string
  ): Promise<{ message: string }> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/conversations/${conversationId}`),
      { method: 'DELETE' }
    )
    const { deleted } = await response.json()
    if (deleted !== true) throw new Error('Failed to delete conversation')
    return { message: 'Conversation deleted successfully' }
  }

  /** FS-539: rename a conversation by setting its user-editable title. */
  async renameConversation(
    conversationId: string,
    title: string
  ): Promise<ChatConversation> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/conversations/${conversationId}`),
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title }),
      }
    )
    return response.json()
  }

  async createConversation(
    request: CreateConversationRequest
  ): Promise<CreateConversationResponse> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/conversations`),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
      }
    )
    const { conversation } = await response.json()
    return { conversation_id: conversation.id }
  }

  /**
   * Checks if the user has any conversations in the current tenant.
   * Endpoint: GET /v1/conversations/has-any
   */
  async hasAnyConversations(): Promise<{ hasConversations: boolean }> {
    const response = await this.fetchOrError(
      this.getUrl('v1/conversations/has-any')
    )
    return response.json()
  }

  async getConversationMessages(conversationId: string): Promise<Message[]> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/conversations/${conversationId}/messages`)
    )
    return (await response.json()).messages
  }

  async sendMessage(
    conversationId: string,
    content: string,
    regenerate: boolean = false,
    timeseriesOptions?: Record<string, unknown>,
    signal?: AbortSignal,
    // Broaden retrieval to the whole catalog (backend: searchEntireCatalog).
    entireDataCatalog: boolean = false
  ): Promise<Response> {
    return this.fetchOrError(this.getChatStreamUrl(conversationId), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // Response-formatting design — declare to the NestJS gateway
        // that this client (ChatInterface) can consume Vercel
        // UIMessageStream v1 frames (text-* / tool-* / data-*). The
        // gateway's `clientSupportsFormattingV2` gate requires this
        // header in addition to the `formatting_v2` flag before it
        // emits the v1 wire format. Missing-header behavior falls
        // back to the legacy SSE shape, so flipping `formatting_v2`
        // is safe for callers that haven't been migrated.
        //
        // Deployment note: PR 17 (#11719) introduced these headers,
        // but its image-build pipeline failed at an unrelated job
        // (`Build data-catalog-web`, since fixed by #11744), and
        // `Deploy to Dev` was skipped. Subsequent main commits all
        // touched backend-only files, so `Detect Changes` kept the
        // frontend image stuck at the pre-PR-17 build with no V2
        // headers. This touch-commit retriggers the frontend deploy
        // path so dev finally serves the post-PR-17 bundle.
        'x-vercel-ai-ui-message-stream': 'v1',
        'x-intuigence-parts': 'v1',
      },
      // `color_scheme` is for what the turn RENDERS: a chart is a standalone
      // document in an iframe, so it cannot pick the theme up from the app's CSS.
      body: JSON.stringify({ content, regenerate, timeseries_options: timeseriesOptions, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, entire_data_catalog: entireDataCatalog, color_scheme: currentColorScheme() }),
      signal,
    },
    // This is a stream, so it opts out of DEFAULT_REQUEST_TIMEOUT_MS — see the
    // note on `sendHelpAgentMessage` for why a wall-clock deadline silently
    // truncates an agent answer instead of surfacing an error.
    { timeoutMs: null })
  }

  // ── Help agent ("Akeera", components/HelpAgent) ─────────────────────
  // Backend surface: backend/web/src/help-agent/ (/v2/help/*). All routes
  // 404 while the `help_agent_enabled` PostHog flag is off for the tenant.

  async createHelpConversation(): Promise<{ conversation_id: string }> {
    // Bodyless POST — no Content-Type so strict proxies don't reject a
    // JSON-typed request with an empty body.
    const response = await this.fetchOrError(this.getUrl('v2/help/conversations'), {
      method: 'POST',
    })
    return response.json()
  }

  /** Streaming send — returns the raw SSE Response (consume via readSSEMessages). */
  async sendHelpAgentMessage(
    conversationId: string,
    body: { content: string; route?: string; language?: string },
    signal?: AbortSignal
  ): Promise<Response> {
    return this.fetchOrError(this.getUrl(`v2/help/conversations/${conversationId}/messages`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal,
    },
    // Stream — opts out of DEFAULT_REQUEST_TIMEOUT_MS.
    //
    // A wall-clock deadline is the wrong instrument for an SSE response. The
    // headers land in milliseconds, so `fetchOrError` has already returned by
    // the time the deadline fires: the caller never sees the "did not respond
    // within Ns" error, because the signal is still attached to the *body*
    // stream and simply kills the reader mid-answer. On dev this cut the agent
    // off at 45s while the backend went on to answer correctly at 50-280s and
    // persist it — the UI showed a permanent spinner and no error.
    //
    // Callers that need to bound a stream pass their own `signal` (ChatInterface
    // does, for stop/unmount), and the backend emits SSE keepalive pings so a
    // genuinely dead connection still surfaces at the transport layer.
    { timeoutMs: null })
  }

  async postHelpAgentFeedback(
    conversationId: string,
    payload: {
      ratings: Record<string, number>
      comment?: string
      message_id?: string
      route?: string
    }
  ): Promise<void> {
    await this.fetchOrError(this.getUrl(`v2/help/conversations/${conversationId}/feedback`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
  }

  async recordThumbsUp(
    conversationId: string,
    messageId: string
  ): Promise<void> {
    await this.addMessageFeedback(conversationId, messageId, {
      value: true,
      message: '',
    })
  }

  async recordThumbsDown(
    conversationId: string,
    messageId: string,
    feedbackText: string = ''
  ): Promise<void> {
    await this.addMessageFeedback(conversationId, messageId, {
      value: false,
      message: feedbackText,
    })
  }

  async addStarRating(
    conversationId: string,
    messageId: string,
    rating: number,
    message?: string,
    screenshot?: string,
    userQuestion?: string,
    assistantAnswer?: string
  ): Promise<void> {
    await this.addMessageFeedback(conversationId, messageId, {
      rating,
      message: message || '',
      screenshot,
      userQuestion,
      assistantAnswer,
    })
  }

  async addMessageFeedback(
    conversationId: string,
    messageId: string,
    feedback: AddFeedbackRequest
  ): Promise<void> {
    await this.fetchOrError(
      this.getUrl(`v1/conversations/${conversationId}/feedback`),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...feedback, message: feedback.message ?? '', message_id: messageId }),
      }
    )
  }

  async removeFeedback(
    conversationId: string,
    messageId: string
  ): Promise<void> {
    await this.fetchOrError(
      this.getUrl(
        `v1/conversations/${conversationId}/messages/${messageId}/feedback`
      ),
      {
        method: 'DELETE',
      }
    )
  }

  async submitDiagnosticFeedback(
    conversationId: string,
    userMessage: string,
    diagnosticContext: any,
    messageId: string
  ): Promise<{ success: boolean; feedbackId?: string }> {
    const response = await this.fetchOrError(
      this.getUrl(
        `v1/conversations/${conversationId}/diagnostic-feedback`
      ),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message_id: messageId,
          user_message: userMessage,
          diagnostic_context: diagnosticContext,
        }),
      }
    )
    return response.json()
  }

  async editMessage(
    conversationId: string,
    messageId: string,
    newContent: string
  ): Promise<Message> {
    const response = await this.fetchOrError(
      this.getUrl(
        `v1/conversations/${conversationId}/messages/${messageId}/edit`
      ),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: newContent }),
      }
    )
    return response.json()
  }

  async getMessageVersions(
    conversationId: string
  ): Promise<MessageVersionGroupResponse[]> {
    const response = await this.fetchOrError(
      this.getUrl(
        `v1/conversations/${conversationId}/message-versions`
      ),
      { method: 'GET' }
    )
    return response.json()
  }

  getChatStreamUrl(conversationId: string): string {
    return this.getUrl(`v1/chat/conversations/${conversationId}/messages`)
  }

  async createWorkspace(
    name: string,
    documents: DocumentPosition[]
  ): Promise<Workspace> {
    const response = await this.fetchOrError(this.getUrl(`v1/workspaces`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, documents }),
    })
    return response.json()
  }

  /**
   * The board list. Pass `page` to bound the response.
   *
   * Unpaged is what the board list used to do unconditionally, and on dev that is
   * 114,766 boards and a 42,964,043-byte body that kills the tab RENDERING it — on
   * the route the app lands on after login, which also holds both "New Board"
   * controls (FS-696). Paging is opt-in so the callers that legitimately want every
   * board — onboarding's "does this user have any?", the eval reaper's sweep — are
   * unchanged.
   *
   * `total` comes from `X-Total-Count` and is the count of every board the list
   * WOULD return, so a bounded page can say what it is a page of. It is null when
   * the caller did not page, because then the array is the whole answer.
   */
  async listWorkspaces(page?: {
    pageNumber: number
    pageSize: number
  }): Promise<{ items: Workspace[]; total: number | null }> {
    const query = page ? `?page_number=${page.pageNumber}&page_size=${page.pageSize}` : ''
    const response = await this.fetchOrError(this.getUrl(`v1/workspaces${query}`))
    const items = (await response.json()) as Workspace[]
    // Optional chaining because the header is read OPPORTUNISTICALLY: an unpaged
    // response carries no total, and the contract above already degrades that to
    // null. A response object without `headers` at all is the same case, and it is
    // reachable — `api.requestTimeout.test.ts` resolves a bare `{ json }` — so
    // throwing here would fail a caller that asked for no total in the first place.
    const header = response.headers?.get('X-Total-Count') ?? null
    const parsed = header === null ? Number.NaN : Number.parseInt(header, 10)
    return { items, total: Number.isInteger(parsed) ? parsed : null }
  }

  async getWorkspace(workspaceId: string): Promise<Workspace> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/workspaces/${workspaceId}`)
    )
    return response.json()
  }

  /**
   * Store a profile photo for the signed-in user (FS-693).
   *
   * `me`, not a user id: the server takes the identity from the token, so there
   * is no way to set someone else's photo. Multipart, same shape as the
   * digital-coworker photo upload.
   */
  async uploadMyAvatar(file: Blob): Promise<{ has_avatar: true }> {
    const formData = new FormData()
    // A filename is required for the server to see a file part at all; the
    // extension is cosmetic since the server fixes the stored one.
    formData.append('photo', file, 'avatar.webp')
    const response = await this.fetchOrError(
      this.getUrl('v1/organization/me/avatar'),
      { method: 'POST', body: formData }
    )
    return response.json()
  }

  /**
   * The signed-in user's uploaded photo, or null when they have not uploaded one.
   *
   * A 404 is the ORDINARY answer here, not a failure — it means "no upload", and
   * the caller falls back to the SSO photo and then to the initial. Returned as
   * a Blob rather than a URL because the endpoint is authed, so an `<img src>`
   * pointed at it would 401; same reason `getWorkspacePreviewImage` does.
   */
  async getMyAvatar(): Promise<Blob | null> {
    // The endpoint answers 204 with an empty body when the user has uploaded
    // nothing, so "no photo" arrives as a SUCCESS and this needs no error
    // handling at all. It used to 404, which meant every boot for every user
    // without an upload — most of them — recorded a failure in client
    // diagnostics before this method could interpret it. A real
    // failure (401, 500) still throws, which is what should happen.
    const response = await this.fetchOrError(this.getUrl('v1/organization/me/avatar'))
    const blob = await response.blob()
    return blob.size > 0 ? blob : null
  }

  /** Forget the uploaded photo, reverting the user to their SSO one. */
  async deleteMyAvatar(): Promise<{ has_avatar: false }> {
    const response = await this.fetchOrError(
      this.getUrl('v1/organization/me/avatar'),
      { method: 'DELETE' }
    )
    return response.json()
  }

  async getWorkspacePreviewImage(workspaceId: string): Promise<Blob> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/workspaces/${workspaceId}/preview`)
    )
    return response.blob()
  }

  async updateWorkspace(
    workspaceId: string,
    updates: UpdateWorkspaceRequest
  ): Promise<Workspace> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/workspaces/${workspaceId}`),
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      }
    )
    return response.json()
  }

  async deleteWorkspace(workspaceId: string): Promise<void> {
    await this.fetchOrError(this.getUrl(`v1/workspaces/${workspaceId}`), {
      method: 'DELETE',
    })
  }

  async duplicateWorkspace(workspaceId: string): Promise<Workspace> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/workspaces/${workspaceId}/duplicate`),
      { method: 'POST' },
    )
    return response.json()
  }

  /**
   * @deprecated Use getWorkspaceTimeseriesTags() instead
   * This endpoint has been removed from the backend
   */
  async getIndustrialDataTags(
    tagType: 'all' | 'assets' | 'timeseries' = 'all'
  ): Promise<{ tags: string[]; count: number }> {
    console.warn('getIndustrialDataTags is deprecated. Use getWorkspaceTimeseriesTags instead.')
    const response = await this.fetchOrError(
      this.getUrl(`v1/industrial-data/tags?tag_type=${tagType}`)
    )
    return response.json()
  }

  /**
   * Get timeseries tags from a workspace (from Postgres)
   */
  async getWorkspaceTimeseriesTags(
    workspaceId: string
  ): Promise<{ tags: string[]; count: number }> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/workspaces/${workspaceId}/timeseries-tags`)
    )
    return response.json()
  }

  /**
   * Get all P&ID tag names for a workspace with source document info.
   */
  async getWorkspacePnidTags(
    workspaceId: string,
    options?: { signal?: AbortSignal }
  ): Promise<{ tags: PnidTagInfo[]; count: number }> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/workspaces/${workspaceId}/pnid-tags`),
      { signal: options?.signal }
    )
    return response.json()
  }

  /**
   * Run on-the-fly entity matching for a workspace given a set of P&ID and
   * timeseries tags. Returns matches with confidence and match_quality.
   */
  async runEntityMatching(
    workspaceId: string,
    body: {
      pid_tags: string[]
      ts_tags: string[]
      confidence_threshold?: number
      use_embeddings?: boolean
      include_unmatched?: boolean
    },
  ): Promise<EntityMatchingResponse> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/workspaces/${workspaceId}/entity-matching`),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    )
    return response.json()
  }

  /**
   * Run tenant-scoped entity matching for a user-selected document set
   * (Data Catalog "Match Entity" flow). The server resolves tags from
   * the selected docs, partitions by type, expands to tenant-wide when
   * a side is empty, and auto-saves results with workspace_id=NULL via
   * the partial unique index on (tenant_id, pid_tag, ts_tag).
   */
  async runTenantEntityMatching(
    body: {
      document_ids: string[]
      confidence_threshold?: number
    },
  ): Promise<TenantMatchingResponse> {
    const response = await this.fetchOrError(
      this.getUrl('v1/tenant/entity-matching'),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    )
    return response.json()
  }

  /**
   * Resolve the P&ID + timeseries tags available from a Data Catalog
   * selection, without running matching. Used by the Match Entity page to
   * populate the pre-run tag lists for docs that haven't been attached to
   * any workspace (the legacy workspace endpoints would return nothing for
   * those).
   */
  async previewTenantMatchTags(
    documentIds: string[],
  ): Promise<{
    pid_tags: string[]
    ts_tags: string[]
    pid_doc_count: number
    ts_doc_count: number
  }> {
    const response = await this.fetchOrError(
      this.getUrl('v1/tenant/entity-matching/preview-tags'),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ document_ids: documentIds }),
      },
    )
    return response.json()
  }

  /**
   * Persist selected entity-match candidates to the workspace. Returns the
   * saved matches with their generated UUIDs (required to attach feedback).
   */
  async saveEntityMatches(
    workspaceId: string,
    matches: EntityMatchCandidate[],
  ): Promise<SavedEntityMatch[]> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/workspaces/${workspaceId}/entity-matches`),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ matches }),
      },
    )
    return response.json()
  }

  /**
   * Record user feedback (accepted / rejected / modified) on a saved match.
   * match_id is the UUID returned by saveEntityMatches.
   */
  async submitMatchFeedback(
    workspaceId: string,
    matchId: string,
    body: {
      action: 'accepted' | 'rejected' | 'modified'
      feedback_notes?: string
      modified_confidence?: number
    },
  ): Promise<unknown> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/workspaces/${workspaceId}/entity-matches/${matchId}/feedback`),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    )
    return response.json()
  }

  /**
   * Record accept/reject feedback on a tenant-scoped (Data Catalog) match.
   * matchId is the persisted row id returned by runTenantEntityMatching.
   */
  async submitTenantMatchFeedback(
    matchId: string,
    body: {
      action: 'accepted' | 'rejected' | 'modified'
      feedback_notes?: string
      modified_confidence?: number
    },
  ): Promise<unknown> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/tenant/entity-matches/${matchId}/feedback`),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    )
    return response.json()
  }

  /**
   * Latest accept/reject decision per tenant-scoped match, so the Match
   * Entity page can restore prior decisions on reopen.
   */
  async getTenantMatchDecisions(): Promise<TenantMatchFeedback[]> {
    const response = await this.fetchOrError(
      this.getUrl('v1/tenant/entity-matches/feedback'),
    )
    return response.json()
  }

  // ---- Tag mapping (FS-655) ----

  /** Counts behind the Matched / Pending tab badges. */
  async getTagMappingSummary(): Promise<TagMappingSummary> {
    const response = await this.fetchOrError(
      this.getUrl('v1/tenant/entity-matching/summary'),
    )
    return response.json()
  }

  /**
   * A page of matched source tags with their matched counterparts. Paginated
   * over source tags, so a tag's matches always arrive together.
   */
  async getMatchedTags(params: {
    limit?: number
    offset?: number
    search?: string
    method?: string
    confidence?: string
    /** Scope to the files on one board (FS-688). */
    boardId?: string
    /** Scope to the tags that occur on one drawing (the Knowledge Graph's Mapped Tag chips). */
    documentId?: string
    /** Column to order the whole set by: pid_tag | mapped_tag | confidence. */
    sortBy?: string
    sortDirection?: 'asc' | 'desc'
  } = {}): Promise<MatchedTagsResponse> {
    const response = await this.fetchOrError(
      this.getUrl('v1/tenant/entity-matching/matched', {
        limit: params.limit,
        offset: params.offset,
        search: params.search || undefined,
        method: params.method || undefined,
        confidence: params.confidence || undefined,
        board_id: params.boardId || undefined,
        document_id: params.documentId || undefined,
        sort_by: params.sortBy || undefined,
        sort_direction: params.sortDirection || undefined,
      }),
    )
    return response.json()
  }

  /** A page of tags with no saved match, each with its nearest candidate. */
  async getPendingTags(params: {
    limit?: number
    offset?: number
    search?: string
    documentId?: string
    source?: string
    /** Scope to the files on one board (FS-688). */
    boardId?: string
    /** Column to order the whole set by; every Pending column sorts. */
    sortBy?: string
    sortDirection?: 'asc' | 'desc'
  } = {}): Promise<PendingTagsResponse> {
    const response = await this.fetchOrError(
      this.getUrl('v1/tenant/entity-matching/pending', {
        limit: params.limit,
        offset: params.offset,
        search: params.search || undefined,
        document_id: params.documentId || undefined,
        source: params.source || undefined,
        board_id: params.boardId || undefined,
        sort_by: params.sortBy || undefined,
        sort_direction: params.sortDirection || undefined,
      }),
    )
    return response.json()
  }

  /**
   * Confirm a discovered tag as real, or rule it out.
   *
   * `tag` is the SPELLING (the row's `display_tag`), not the core key — the
   * server derives the identity so two callers cannot disagree about what the
   * same device is. A confirmation reaches the assistant through the registry's
   * ordinary republish; a rejection suppresses the identity instead.
   */
  async recordTagVerdict(body: {
    tag: string
    verdict: 'confirmed' | 'not_a_tag'
    note?: string
  }): Promise<TagVerdictResponse> {
    const response = await this.fetchOrError(
      this.getUrl('v1/tenant/entity-matching/verdict'),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    )
    return response.json()
  }

  /** Timeseries tags the given P&ID tag can be connected to, best first. */
  async getConnectCandidates(params: {
    tag: string
    documentId?: string | null
    search?: string
    limit?: number
  }): Promise<{ candidates: ConnectCandidate[] }> {
    const response = await this.fetchOrError(
      this.getUrl('v1/tenant/entity-matching/connect-candidates', {
        tag: params.tag,
        document_id: params.documentId || undefined,
        search: params.search || undefined,
        limit: params.limit,
      }),
    )
    return response.json()
  }

  /**
   * Connect a P&ID tag to a timeseries tag by hand. The server also records an
   * `accepted` decision — a person picking the target IS the review.
   */
  async connectTag(body: {
    pid_tag: string
    ts_tag: string
    pid_document_id?: string | null
    /** The match this connection replaces (Edit on a Matched row); rejected in the same request. */
    replaces_match_id?: string | null
  }): Promise<{
    match: TenantMatchResultRow
    /**
     * The match the server retired. Absent from a backend that predates
     * `replaces_match_id` (it ignores the field), so the dialog checks this
     * before it says "replaced".
     */
    replaced_match_id?: string | null
  }> {
    const response = await this.fetchOrError(
      this.getUrl('v1/tenant/entity-matching/connect'),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    )
    return response.json()
  }

  /** Every sheet a tag appears on, for the Tag mapping drill-down. */
  async getTagSheets(tag: string): Promise<{ tag: string; sheets: TagSheetRow[] }> {
    const response = await this.fetchOrError(
      this.getUrl('v1/tenant/entity-matching/tag-sheets', { tag }),
    )
    return response.json()
  }

  /** Every document passage that names the tag, for the Documents tab. */
  async getTagDocuments(
    tag: string,
  ): Promise<{ tag: string; documents: TagDocumentRow[] }> {
    const response = await this.fetchOrError(
      this.getUrl('v1/tenant/entity-matching/tag-documents', { tag }),
    )
    return response.json()
  }

  /**
   * Get timeseries metadata for a document
   */
  async getTimeseriesMetadata(docId: string): Promise<unknown> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/document/${docId}/timeseries/metadata`)
    )
    return response.json()
  }

  /**
   * Get timeseries tags for a document
   */
  async getTimeseriesTags(docId: string): Promise<unknown> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/document/${docId}/timeseries/tags`)
    )
    return response.json()
  }

  async getDocumentThumbnail(docId: string): Promise<{ blob: Blob; originalWidth: number }> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/index/${docId}/thumbnail`)
    )
    const originalWidth = parseInt(response.headers.get('X-Original-Width') || '0', 10)
    const blob = await response.blob()
    // An empty body arrives as a SUCCESS, so it has to be rejected here or it
    // becomes an object URL that no `<img>` can decode. `getMyAvatar` guards the
    // same way for the same reason (a 204 with no body); this path did not, so a
    // zero-byte thumbnail reached the DOM as a broken image rather than a clean
    // failure the caller could fall back from. Throwing — rather than returning
    // null — is what lets `fetchSheetThumbnail` try the drawing image instead.
    if (blob.size === 0) {
      throw new Error(`Thumbnail for ${docId} was empty (0 bytes)`)
    }
    return { blob, originalWidth }
  }

  /**
   * Fetch the per-type palette the backend uses to paint graph thumbnail
   * annotations. Single source of truth so the server-rendered PNG
   * (Strategy 2 in `DocumentIcon::fetchThumbnail`) and the client-side
   * composite (Strategy 3) cannot drift — same `node_type → color`
   * dict in both places, sourced from `GRAPH_NODE_COLORS` in the Python
   * `thumbnail_routes.py`. `default` is what unknown types fall back to
   * (FS-482 user call: "if no color specified, just have blue").
   *
   * Cacheable via browser cache (Cache-Control: 86400s) so this is
   * effectively a one-shot call per session.
   */
  async getThumbnailAnnotationPalette(): Promise<{
    colors: Record<string, string>
    default: string
  }> {
    const response = await this.fetchOrError(
      this.getUrl('v1/thumbnail/annotation-color')
    )
    return response.json()
  }

  async getDocumentPage(docId: string, page: number): Promise<Blob> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/index/${docId}/page/${page}/content`)
    )
    return response.blob()
  }

  async getDocumentPageText(docId: string, page: number): Promise<string> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/index/${docId}/page/${page}/text`)
    )
    return response.json()
  }

  async getDocumentPageSummary(docId: string, page: number): Promise<{
    summary: string
    key_points: string[]
    document_type: string
    word_count: number
    char_count: number
  }> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/index/${docId}/page/${page}/summary`)
    )
    return response.json()
  }

  async updateDocumentPageText(
    docId: string,
    page: number,
    text: string
  ): Promise<{ success: boolean; doc_id: string; page: number }> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/index/${docId}/page/${page}/text`),
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text }),
      }
    )
    return response.json()
  }

  /**
   * FS-557 — save-back for a transcript artifact. Overwrites the canonical
   * markdown body (documents.location) + the KG layout.md and re-ingests, so
   * the edit persists on reload and the kg-agent tracks it. Distinct from
   * `updateDocumentPageText`, which only writes the per-page OCR text store.
   */
  async updateTranscriptContent(
    docId: string,
    content: string
  ): Promise<{ status: string; documentId: string }> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/documents/${docId}/content`),
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content }),
      }
    )
    return response.json()
  }

  async getDocumentInfo(docId: string): Promise<any> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/index/${docId}/info`)
    )
    return response.json()
  }

  async getPageWordPositions(docId: string, page: number): Promise<PageWordPositions> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/index/${docId}/page/${page}/word-positions`)
    )
    return response.json()
  }

  async searchDocuments(
    query: string,
    documentIds: string[]
  ): Promise<SearchResponse> {
    const response = await this.fetchOrError(this.getUrl(`v1/index/search`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ docs: documentIds, query }),
    })
    return response.json()
  }

  async searchAllDocuments(query: string): Promise<SearchResponse> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/index/search-all?query=${encodeURIComponent(query)}`)
    )
    return response.json()
  }

  // Visual Elements APIs for diagram-aware document processing

  async getVisualElementImage(
    elementId: string,
    thumbnail: boolean = false
  ): Promise<Blob> {
    const params = thumbnail ? '?thumbnail=true' : ''
    const response = await this.fetchOrError(
      this.getUrl(`v1/visual-elements/element/${elementId}/image${params}`)
    )
    return response.blob()
  }

  async searchVisualElements(
    query: string,
    documentId?: string,
    elementType?: string,
    limit: number = 20
  ): Promise<VisualElementSummary[]> {
    const params = new URLSearchParams({ query, limit: limit.toString() })
    if (documentId) params.append('document_id', documentId)
    if (elementType) params.append('element_type', elementType)

    const response = await this.fetchOrError(
      this.getUrl(`v1/visual-elements/search?${params}`)
    )
    return response.json()
  }

  /**
   * @param opts.timeoutMs Abort the request after this many ms. Defaults to
   *   {@link DEFAULT_REQUEST_TIMEOUT_MS}. Pass `null` to wait indefinitely —
   *   required for SSE streams and large uploads/downloads, which legitimately
   *   outlive any request-level deadline.
   */
  async fetchOrError(
    input: string | URL,
    init?: RequestInit,
    opts?: { timeoutMs?: number | null }
  ): Promise<Response> {
    // Generate correlation ID before try so it's available in catch for diagnostics
    const correlationId = generateCorrelationId()
    let recorded = false

    // INFRA-173: without a deadline the UI waits forever. When
    // GET /v1/workspaces degraded to 50s (staging) / 685s (dev), Kong retried it
    // onto the sibling replica at 60s and the browser simply sat with no response
    // headers for 92s, then aborted — the walkthrough recorded a permanent freeze
    // with no error to show a user. A request that has outlived any plausible
    // healthy duration is a failure; the UI's job is to say so, not to wait.
    const timeoutMs = opts?.timeoutMs === undefined ? DEFAULT_REQUEST_TIMEOUT_MS : opts.timeoutMs
    const timeoutSignal = timeoutMs === null ? undefined : AbortSignal.timeout(timeoutMs)

    try {
      // In Fabric embed mode, wait for PostMessage token from parent window
      let token: string | null
      if (isFabricEmbedMode()) {
        token = await waitForEmbedToken()
      } else {
        // Always call ensureFreshToken to handle expired tokens, not just missing ones
        token = await ensureFreshToken()
      }

      const headers: Record<string, string> = {
        ...init?.headers as Record<string, string>,
        [CORRELATION_ID_HEADER]: correlationId,
        Authorization: token ? `Bearer ${token}` : '',
      }

      // In embed mode, include workspace ID for tenant resolution
      if (isFabricEmbedMode()) {
        const workspaceId = getEmbedWorkspaceId()
        if (workspaceId) {
          headers['X-Fabric-Workspace-Id'] = workspaceId
        }
      }

      const response = await fetch(input, {
        ...init,
        headers,
        // Merged, not replaced: 37 call sites pass their own signal to cancel on
        // unmount or supersede an in-flight request, and dropping either half
        // would silently break one of the two.
        signal: combineAbortSignals(init?.signal, timeoutSignal),
      })

      // Record API call for bug report diagnostics
      recorded = true
      recordApiCall({
        correlationId,
        url: typeof input === 'string' ? input : input.toString(),
        method: init?.method || 'GET',
        status: response.status,
        timestamp: Date.now(),
      })

      // In embed mode, signal token expired on 401 so parent can refresh
      if (!response.ok && response.status === 401 && isFabricEmbedMode()) {
        signalTokenExpired()
      }

      if (!response.ok) {
        let errorMessage = response.statusText
        let errorCode: string | undefined
        let featureRefused = false
        try {
          const errorData = await response.json()
          featureRefused = errorData?.code === FEATURE_NOT_ENABLED_CODE
          // Extract error message from 'detail' (FastAPI validation),
          // 'error' (legacy API errors), or 'error.message' (OpenAI-style
          // envelope produced by NestJS `OpenAiErrorFilter` for every
          // `/v1/*` route).
          //
          // Pre-fix: `errorData.error` was assumed to be a string. The
          // OpenAI envelope wraps it as `{message, type, code, param}` —
          // an object. `errorMessage = {object}` then flowed into
          // `new Error(errorMessage)`, which coerces via `String(obj)`
          // to the literal `"[object Object]"`. Users saw
          // `Could not load section: [object Object]` instead of the
          // real backend message (e.g. "Resolver-data sidecar not
          // present for this tenant. Has kg-regen run?").
          // Surfaced on RegistryTab (Gap #18b) live 2026-05-19.
          errorMessage = extractErrorMessage(errorData) ?? errorMessage
          // Extract error_code for structured error handling (CSV errors, etc.)
          if (errorData.error_code) {
            errorCode = errorData.error_code
          } else if (
            errorData.error &&
            typeof errorData.error === 'object' &&
            typeof (errorData.error as { code?: unknown }).code === 'string'
          ) {
            errorCode = (errorData.error as { code: string }).code
          }
        } catch {
          // If parsing fails, use statusText
        }

        // Annotators are read-only outside the P&ID editor. Degrade gracefully
        // ONLY on the AnnotatorPolicyGuard's own 403 (matched by its message) —
        // not on unrelated resource/org-level 403s, which would otherwise show a
        // misleading "read-only" toast. Mirrors the guard's ForbiddenException
        // message; keep the two strings in sync.
        if (
          response.status === 403 &&
          isAnnotatorRole(getUserInfo()?.role) &&
          errorMessage === ANNOTATOR_READONLY_MESSAGE
        ) {
          notifyAnnotatorBlocked(errorMessage)
        }

        // A per-person feature flag switched this area off (FeatureAccessGuard).
        // /v1 routes wrap errors in an envelope that drops `code`, so the message counts too.
        if (response.status === 403 && (featureRefused || errorMessage === FEATURE_NOT_ENABLED_MESSAGE)) {
          notifyFeatureRefusedByApi()
        }

        // Create error with response data (similar to axios error structure)
        const error = new Error(errorMessage) as Error & {
          response: {
            status: number
            statusText: string
            data: string
            // FS-482: honor server-issued Retry-After on 503 so callers
            // can back off the cadence the server requested instead of
            // a hardcoded client-side constant that drifts.
            retryAfterSeconds?: number
          }
          error_code?: string
        }
        // RFC 7231: Retry-After is delta-seconds or HTTP-date. Only
        // delta-seconds (a positive integer) is forwarded; date-form is
        // ignored since none of our callers know how to apply it.
        const retryAfterHeader = response.headers?.get('Retry-After')
        const retryAfterSeconds = retryAfterHeader && /^\d+$/.test(retryAfterHeader.trim())
          ? parseInt(retryAfterHeader, 10)
          : undefined
        error.response = {
          status: response.status,
          statusText: response.statusText,
          data: errorMessage,
          ...(retryAfterSeconds !== undefined ? { retryAfterSeconds } : {}),
        }
        // Attach error_code if available for structured error handling
        if (errorCode) {
          error.error_code = errorCode
        }
        throw error
      }
      return response
    } catch (error) {
      // Record failed API call only if not already recorded (avoids duplicates for HTTP errors)
      if (!recorded) {
        recordApiCall({
          correlationId,
          url: typeof input === 'string' ? input : input.toString(),
          method: init?.method || 'GET',
          status: null,
          timestamp: Date.now(),
        })
      }

      // Our own deadline fired. `AbortSignal.timeout` rejects with a bare
      // TimeoutError DOMException whose message ("signal timed out") tells a user
      // nothing, so restate it in terms of what happened and stays distinguishable
      // from a caller-initiated cancel — which is an AbortError and is NOT an
      // error worth reporting.
      if (error instanceof DOMException && error.name === 'TimeoutError') {
        const timeout = new Error(
          `The server did not respond within ${Math.round((timeoutMs as number) / 1000)}s. It may be overloaded — try again.`
        ) as Error & { isTimeout: true }
        timeout.isTimeout = true
        recordError(timeout.message, timeout.stack)
        console.error('[api-timeout]', timeout)
        throw timeout
      }

      // Record error for bug report diagnostics
      if (error instanceof Error && error.name !== 'AbortError') {
        recordError(error.message, error.stack)
        console.error('[api-error]', error)
      }
      throw error
    }
  }

  async queryDatasheet(
    docId: string,
    sql: string,
    params: Record<string, string | number | boolean> = {}
  ): Promise<QueryResult> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/document/${docId}/query_datasheet`),
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ sql, params }),
      }
    )
    return response.json()
  }

  async getDatasheetSheets(docId: string): Promise<SheetsResponse> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/document/${docId}/sheets`)
    )
    return response.json()
  }

  /**
   * The first rows of a datasheet or timeseries file, trimmed server-side, for
   * the board card. Read from the preview ingest saved; 404 when there is none.
   * Endpoint: GET v1/document/:id/preview-head
   */
  async getSheetPreviewHead(docId: string, opts: { rows: number; cols: number }): Promise<SheetPreviewHead> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/document/${docId}/preview-head?rows=${opts.rows}&cols=${opts.cols}`)
    )
    return response.json()
  }

  async getDatasheetPreview(docId: string, sheet: string): Promise<QueryResult> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/document/${docId}/datasheet_preview?sheet=${encodeURIComponent(sheet)}`)
    )
    return response.json()
  }

  /**
   * GET /v1/knowledge-graph/reprocessing — FS-637 status for the caller's tenant.
   *
   * Tenant-scoped on purpose; the cross-tenant fleet table is the token-guarded
   * backoffice endpoint, not this one.
   */
  async getReprocessingStatus(): Promise<ReprocessingStatus> {
    const response = await this.fetchOrError(this.getUrl('v1/knowledge-graph/reprocessing'))
    return response.json()
  }

  /** PUT /v1/knowledge-graph/reprocessing/policy — the auto/always/never control. */
  async setReprocessingPolicy(policy: ReparsePolicy): Promise<{ policy: ReparsePolicy }> {
    const response = await this.fetchOrError(
      this.getUrl('v1/knowledge-graph/reprocessing/policy'),
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ policy }),
      },
    )
    return response.json()
  }

  // Egress-consent policy (Trust/Security §9 v1)

  /** GET /v1/agent/egress-policy — defaults (always_allow) when never saved. */
  async getEgressPolicy(): Promise<EgressPolicy> {
    const response = await this.fetchOrError(this.getUrl('v1/agent/egress-policy'))
    return response.json()
  }

  /** PUT /v1/agent/egress-policy — admin-only; merges the patch server-side. */
  async updateEgressPolicy(patch: EgressPolicyPatch): Promise<EgressPolicy> {
    const response = await this.fetchOrError(this.getUrl('v1/agent/egress-policy'), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    })
    return response.json()
  }

  /** GET a conversation's stored egress-consent verdicts (FS-676); {} = never asked. */
  async getEgressConsent(conversationId: string): Promise<EgressConsentVerdicts> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/agent/conversations/${conversationId}/egress-consent`)
    )
    return response.json()
  }

  /** PUT the user's answer to a consent prompt — conversation-owner-only. */
  async updateEgressConsent(
    conversationId: string,
    write: EgressConsentWrite
  ): Promise<EgressConsentWrite> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/agent/conversations/${conversationId}/egress-consent`),
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(write),
      }
    )
    return response.json()
  }

  // Tenant Agent Management

  /**
   * Get all agents with provisioning/enablement status for the current tenant.
   * Endpoint: GET /v1/organization/agents
   */
  async getTenantAgents(): Promise<TenantAgentsResponse> {
    const response = await this.fetchOrError(
      this.getUrl('v1/organization/agents')
    )
    return response.json()
  }

  /**
   * Enable or disable an agent for the current tenant.
   * Requires admin role.
   * Endpoint: PATCH /v1/organization/agents/:agentId
   */
  async updateTenantAgent(
    agentId: string,
    enabled: boolean
  ): Promise<TenantAgent> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/organization/agents/${encodeURIComponent(agentId)}`),
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled }),
      }
    )
    return response.json()
  }

  /**
   * List custom display names for the current user.
   * Endpoint: GET /v1/user/agent-names
   */
  async getUserAgentNames(): Promise<UserAgentNameEntry[]> {
    const response = await this.fetchOrError(this.getUrl('v1/user/agent-names'))
    return response.json()
  }

  /**
   * Set or update custom display name for an agent.
   * Endpoint: PUT /v1/user/agent-names/:agentId
   */
  async upsertUserAgentName(
    agentId: string,
    customName: string
  ): Promise<UserAgentNameEntry> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/user/agent-names/${encodeURIComponent(agentId)}`),
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customName }),
      }
    )
    return response.json()
  }

  /**
   * Clear custom name for an agent (revert to default).
   * Endpoint: DELETE /v1/user/agent-names/:agentId
   */
  async deleteUserAgentName(agentId: string): Promise<void> {
    await this.fetchOrError(
      this.getUrl(`v1/user/agent-names/${encodeURIComponent(agentId)}`),
      { method: 'DELETE' }
    )
  }

  // Platform Admin: Agent Provisioning

  /**
   * List all available agent personas (id + name).
   * Platform admin only.
   * Endpoint: GET /v1/platform/agents
   */
  async listAvailableAgents(): Promise<{ id: string; name: string }[]> {
    const response = await this.fetchOrError(
      this.getUrl('v1/platform/agents')
    )
    return response.json()
  }

  /**
   * List all tenants with their agent provisioning summaries.
   * Platform admin only.
   * Endpoint: GET /v1/platform/tenants/agents
   */
  async listTenantProvisioning(): Promise<TenantProvisioningSummary[]> {
    const response = await this.fetchOrError(
      this.getUrl('v1/platform/tenants/agents')
    )
    return response.json()
  }

  /**
   * Update which agents are provisioned to a specific tenant.
   * Platform admin only.
   * Endpoint: PATCH /v1/platform/tenants/:tenantId/agents
   */
  async updateTenantProvisioning(
    tenantId: string,
    provisionedAgents: string[]
  ): Promise<TenantProvisioningSummary> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/platform/tenants/${encodeURIComponent(tenantId)}/agents`),
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ provisioned_agents: provisionedAgents }),
      }
    )
    return response.json()
  }

  // Connector related methods

  /**
   * Gets the list of connectors for a tenant.
   * Endpoint: GET v1/connector/list
   */
  async getConnectors(): Promise<ConnectorListResponse> {
    const response = await this.fetchOrError(this.getUrl(`v1/connector/list`))
    return response.json()
  }

  /**
   * Gets a specific connector.
   * Endpoint: GET v1/connector/{connector_id}
   */
  async getConnector(connectorId: string): Promise<ConnectorResponse> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/connector/${connectorId}`)
    )
    return response.json()
  }

  /**
   * Probe whether the eventhouse self-service lane is available for this
   * tenant. The /v2/eventhouse surface is flag-gated server-side
   * (eventhouse_readings_enabled): a 403 means "feature off" — anything else
   * rethrows so a transient outage is never cached as unavailable (mirrors
   * the Fabric workload's isEventhouseLiveLinkAvailable).
   * Endpoint: GET v2/eventhouse/connections
   */
  async isEventhouseAvailable(): Promise<boolean> {
    try {
      await this.fetchOrError(this.getUrl(`v2/eventhouse/connections`))
      return true
    } catch (error) {
      const status = (error as { response?: { status?: number } }).response
        ?.status
      if (status === 403) return false
      throw error
    }
  }

  /**
   * List the tenant's Eventhouse live-link connections. Same flag-gated
   * surface as isEventhouseAvailable above, but returns the rows: the
   * live-stream viewer uses them to show where a stream points and when its
   * tag inventory was last refreshed. Callers that must render without
   * connection details (feature off for the tenant → 403) should catch and
   * fall back to [].
   * Endpoint: GET v2/eventhouse/connections
   */
  async listEventhouseConnections(): Promise<EventhouseConnectionSummary[]> {
    const response = await this.fetchOrError(this.getUrl(`v2/eventhouse/connections`))
    return response.json()
  }

  /**
   * One page of the RAW rows behind a live-stream anchor document, read live
   * from the tenant's Eventhouse (nothing is copied into our database).
   *
   * `cursor` is the opaque `next_cursor` from the previous page — keyset
   * pagination, because Kusto has no OFFSET. Omit it for the first page and
   * stop when `has_more` is false.
   * Endpoint: GET v2/eventhouse/documents/{docId}/rows
   */
  async getEventhouseRows(
    docId: string,
    limit = 100,
    cursor?: string | null
  ): Promise<EventhouseReadingsPage> {
    const params = new URLSearchParams({ limit: String(limit) })
    if (cursor) params.set('cursor', cursor)
    const response = await this.fetchOrError(
      this.getUrl(`v2/eventhouse/documents/${docId}/rows?${params.toString()}`)
    )
    return response.json()
  }

  /**
   * Advance an eventhouse connector's provisioning state machine (link a
   * seeded provision / retry a failed one). Idempotent for other states.
   * Endpoint: POST v1/connector/{connector_id}/provision
   */
  async advanceConnectorProvision(
    connectorId: string
  ): Promise<ConnectorResponse> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/connector/${connectorId}/provision`),
      { method: 'POST' }
    )
    return response.json()
  }

  /**
   * Sends a heartbeat for a connector.
   * Endpoint: POST v1/connector/{connector_id}/heartbeat
   */
  async heartbeatConnector(
    connectorId: string,
    requestBody: ConnectorHeartbeatRequest
  ): Promise<ConnectorHeartbeatResponse> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/connector/${connectorId}/heartbeat`),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      }
    )
    return response.json()
  }

  /**
   * Creates a new connector.
   * Endpoint: POST v1/connector
   */
  async createConnector(
    requestBody: ConnectorRequest
  ): Promise<ConnectorIdResponse> {
    const response = await this.fetchOrError(this.getUrl(`v1/connector`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody),
    })
    return response.json()
  }

  /**
   * Updates an existing connector.
   * Endpoint: PUT v1/connector/{connector_id}
   */
  async updateConnector(
    connectorId: string,
    requestBody: ConnectorUpdateRequest
  ): Promise<ConnectorUpdateResponse> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/connector/${connectorId}`),
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      }
    )
    return response.json()
  }

  /**
   * Activates or deactivates a connector.
   * Endpoint: PUT v1/connector/{connector_id}/activate
   */
  async activateConnector(
    connectorId: string,
    requestBody: ConnectorActivateRequest
  ): Promise<boolean> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/connector/${connectorId}/activate`),
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      }
    )
    return response.json()
  }

  /**
   * Deletes a connector.
   * Endpoint: DELETE v1/connector/{connector_id}
   */
  async deleteConnector(connectorId: string): Promise<boolean> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/connector/${connectorId}`),
      { method: 'DELETE' }
    )
    return response.json()
  }

  /**
   * Requests AF discovery for a connector.
   * Endpoint: POST v1/connector/{connector_id}/discover
   */
  async requestConnectorDiscovery(
    connectorId: string,
    requestBody: ConnectorDiscoverRequest
  ): Promise<ConnectorDiscoverResponse> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/connector/${connectorId}/discover`),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      }
    )
    return response.json()
  }
  // Finished connector related methods

  // Connected P&IDs of a graph (pages, grouped siblings, D2D links, manual connectors)
  async getConnectedGraphs(graphId: string): Promise<ConnectedGraphsResponse> {
    const response = await this.fetchOrError(this.getUrl(`v1/graph/${graphId}/connected`))
    return response.json()
  }

  // Sheet connector related methods

  async listSheetConnectors(documentId: string): Promise<SheetConnectorListResponse> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/sheet-connectors?document_id=${documentId}`),
    )
    return response.json()
  }

  async createSheetConnector(dto: CreateSheetConnectorRequest): Promise<SheetConnector> {
    const response = await this.fetchOrError(
      this.getUrl('v1/sheet-connectors'),
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(dto) },
    )
    return response.json()
  }

  async createSheetConnectorWithMirror(dto: CreateSheetConnectorRequest): Promise<CreateWithMirrorResponse> {
    const response = await this.fetchOrError(
      this.getUrl('v1/sheet-connectors/with-mirror'),
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(dto) },
    )
    return response.json()
  }

  async updateSheetConnector(id: string, dto: UpdateSheetConnectorRequest): Promise<SheetConnector> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/sheet-connectors/${id}`),
      { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(dto) },
    )
    return response.json()
  }

  async deleteSheetConnector(id: string): Promise<boolean> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/sheet-connectors/${id}`),
      { method: 'DELETE' },
    )
    return response.json()
  }

  async bulkSetSheetConnectors(documentId: string, connectors: CreateSheetConnectorRequest[]): Promise<SheetConnectorListResponse> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/sheet-connectors/bulk?document_id=${documentId}`),
      { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ connectors }) },
    )
    return response.json()
  }

  // Finished sheet connector related methods

  // Job / Job Run related methods

  /**
   * Fetches a list of jobs for a given tenant.
   * Endpoint: GET v1/job/list
   */
  async getJobs(
    pageSize: number,
    pageNumber: number,
    executorId?: string,
    status?: JobStatusType
  ): Promise<ListResponse<JobRecord>> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/job/list`, {
        page_size: pageSize,
        page_number: pageNumber,
        executor_id: executorId,
        status,
      })
    )
    return response.json()
  }

  /**
   * Fetches the job details for a given job ID.
   * Endpoint: GET `v1/job/{jobId}`
   */
  async getJob(jobId: string): Promise<JobResponse> {
    const response = await this.fetchOrError(this.getUrl(`v1/job/${jobId}`))
    return response.json()
  }

  /**
   * Creates a new job.
   * Endpoint: POST `v1/job`
   */
  async createJob(request: JobRequest): Promise<JobIdResponse> {
    const response = await this.fetchOrError(this.getUrl(`v1/job`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    })
    return response.json()
  }

  /**
   * Fetches the list of job runs for a given job ID.
   * Endpoint: GET `v1/job/${jobId}/run/list`
   */
  async getJobRuns(
    jobId: string,
    latest: boolean = false
  ): Promise<ListResponse<JobRunRecord>> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/job/${jobId}/run/list`, { latest })
    )
    return response.json()
  }

  /**
   * Fetches the details of a specific job run.
   * Endpoint: GET `v1/job/${jobId}/run/${runId}`
   */
  async getJobRun(jobId: string, runId: string): Promise<JobRunResponse> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/job/${jobId}/run/${runId}`)
    )
    return response.json()
  }
  // Finished Job / Job Run related methods

  async getVersionHistory(
    graphId: string
  ): Promise<Array<{ version: number; created_at: string; user_id: string }>> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/graph/${graphId}/versions`)
    )
    return response.json()
  }
  // Expert Knowledge API Methods

  /**
   * Fetches all interviews for the current tenant.
   * Endpoint: GET `v1/interviews`
   */
  async listInterviews(): Promise<Interview[]> {
    const response = await this.fetchOrError(this.getUrl('v1/interviews'))
    return response.json()
  }

  /**
   * Fetches an interview by ID.
   * Endpoint: GET `v1/interviews/{id}`
   */
  async getInterview(id: string): Promise<Interview> {
    const response = await this.fetchOrError(this.getUrl(`v1/interviews/${id}`))
    return response.json()
  }

  /**
   * Creates a new interview.
   * Endpoint: POST `v1/interviews`
   */
  async createInterview(
    data: Omit<InterviewCreate, 'webapp_url'>
  ): Promise<Interview> {
    const requestData: InterviewCreate = {
      ...data,
      webapp_url: `${window.location.origin}/meeting`,
    }

    const response = await this.fetchOrError(this.getUrl('v1/interviews'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestData),
    })
    return response.json()
  }

  /**
   * Deletes an interview.
   * Endpoint: DELETE `v1/interviews/{id}`
   */
  async deleteInterview(id: string): Promise<{ message: string }> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/interviews/${id}`),
      {
        method: 'DELETE',
      }
    )
    return response.json()
  }

  /**
   * Creates a new organization via Keycloak.
   * Backend creates the organization and adds user as admin.
   * Endpoint: POST /v1/organization
   */
  async createOrganization(requestBody: {
    name: string
    country?: string | null
    size?: number | null
  }): Promise<{
    id: string
    tenant_id: string
    name: string
    created_at: string
  }> {
    const response = await this.fetchOrError(this.getUrl('v1/organization'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    })
    return response.json()
  }

  // Interview Document API Methods

  /**
   * Uploads a document to an interview.
   * Endpoint: POST `v1/interviews/{interview_id}/documents/upload`
   */
  async uploadInterviewDocument(
    file: File,
    interviewId: string,
    metadata: InterviewDocumentMetadata
  ): Promise<InterviewDocument> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('metadata', JSON.stringify(metadata))

    const response = await this.fetchOrError(
      this.getUrl(`v1/interviews/${interviewId}/documents/upload`),
      {
        method: 'POST',
        body: formData,
      },
      // A bulk transfer — see uploadFile.
      { timeoutMs: null }
    )
    return response.json()
  }

  /**
   * Lists all documents for an interview.
   * Endpoint: GET `v1/interviews/{interview_id}/documents`
   */
  async listInterviewDocuments(interviewId: string): Promise<InterviewDocument[]> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/interviews/${interviewId}/documents`)
    )
    return response.json()
  }

  /**
   * Downloads a document from an interview.
   * Endpoint: GET `v1/interviews/{interview_id}/documents/{document_id}/download`
   */
  async downloadInterviewDocument(
    interviewId: string,
    documentId: string
  ): Promise<Blob> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/interviews/${interviewId}/documents/${documentId}/download`),
      undefined,
      // A whole document body — see downloadDocument.
      { timeoutMs: null }
    )
    return response.blob()
  }

  /**
   * Deletes a document from an interview.
   * Endpoint: DELETE `v1/interviews/{interview_id}/documents/{document_id}`
   */
  async deleteInterviewDocument(
    interviewId: string,
    documentId: string
  ): Promise<{ message: string }> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/interviews/${interviewId}/documents/${documentId}`),
      { method: 'DELETE' }
    )
    return response.json()
  }

  // Organization API methods

  // getCurrentUser removed - use authSessionSlice.user instead (JWT is source of truth)

  /**
   * Get all users in the current organization.
   * Fetches users from Keycloak via backend.
   * Endpoint: GET /v1/organization/users
   */
  async getOrganizationUsers(): Promise<{
    tenant_id: string
    users: Array<{
      user_id: string | null
      invite_id?: string | null
      email: string | null
      name: string | null
      picture: string | null
      role: string
      status: string
      created_at: string | null
      invited_at: string | null
    }>
  }> {
    const response = await this.fetchOrError(
      this.getUrl('v1/organization/users'),
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      }
    )
    return response.json()
  }

  /**
   * Searches users in the organization by name or email.
   * Endpoint: GET /v1/organization/users/search?q={query}
   */
  async searchOrganizationUsers(query: string): Promise<
    Array<{
      user_id: string
      email: string | null
      name: string | null
      picture: string | null
      role: string
      status: string
      joined_at: string | null
    }>
  > {
    const response = await this.fetchOrError(
      this.getUrl(`v1/organization/users/search?q=${encodeURIComponent(query)}`)
    )
    return response.json()
  }

  /**
   * FS-663: share a board or chat with one or more people.
   *
   * A single batched call rather than one per recipient — the server fans out
   * the org invites and the notification emails together and returns a
   * per-recipient outcome, so the UI can badge "invited" vs "shared" vs
   * "couldn't email them" without a refetch race.
   * Endpoint: POST /v1/shares
   */
  async createShares(requestBody: CreateSharesRequest): Promise<CreateSharesResponse> {
    const response = await this.fetchOrError(this.getUrl('v1/shares'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody),
    })
    return response.json()
  }

  /**
   * Who currently has access to a resource.
   * Endpoint: GET /v1/shares?resource_type=&resource_id=
   */
  async listShares(
    resourceType: ShareResourceType,
    resourceId: string
  ): Promise<{ shares: Share[] }> {
    const response = await this.fetchOrError(
      this.getUrl(
        `v1/shares?resource_type=${encodeURIComponent(resourceType)}&resource_id=${encodeURIComponent(resourceId)}`
      )
    )
    return response.json()
  }

  /**
   * What has been shared WITH me (live grants, newest first).
   *
   * Used by the invited-recipient landing: Keycloak's invitation email links to
   * the bare app root, so there is no deep link for the client to resume and it
   * must ask the server what the invite was about.
   * Endpoint: GET /v1/shares/mine
   */
  async getMyPendingShares(): Promise<{ shares: Share[] }> {
    const response = await this.fetchOrError(this.getUrl('v1/shares/mine'))
    return response.json()
  }

  /**
   * Revoke a share (soft — the grant is marked revoked, not deleted).
   * Endpoint: DELETE /v1/shares/{id}
   */
  async deleteShare(shareId: string): Promise<{ revoked: boolean }> {
    const response = await this.fetchOrError(this.getUrl(`v1/shares/${shareId}`), {
      method: 'DELETE',
    })
    return response.json()
  }

  /**
   * Invites a user to the organization.
   * Endpoint: POST /v1/organization/invite
   */
  async inviteUserToOrganization(requestBody: {
    email: string
    role: UserRole
  }): Promise<{
    user_id: string
    email: string
    tenant_id: string
    role: string
    message: string
  }> {
    const response = await this.fetchOrError(
      this.getUrl('v1/organization/invite'),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      }
    )
    return response.json()
  }

  /**
   * Updates a user's role in the organization.
   * Endpoint: PATCH /v1/organization/user/{user_id}
   */
  async updateUserRole(
    userId: string,
    requestBody: UpdateUserRequest
  ): Promise<UpdateUserResponse> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/organization/user/${userId}`),
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      }
    )
    return response.json()
  }

  /**
   * Removes a user from the organization.
   * Endpoint: DELETE /v1/organization/user/{user_id}
   */
  async deleteOrganizationUser(userId: string): Promise<void> {
    await this.fetchOrError(this.getUrl(`v1/organization/user/${userId}`), {
      method: 'DELETE',
    })
  }

  /**
   * Revokes a pending organization invitation.
   * Endpoint: DELETE /v1/organization/invite/{invite_id}
   */
  async revokeOrganizationInvite(inviteId: string): Promise<void> {
    await this.fetchOrError(this.getUrl(`v1/organization/invite/${inviteId}`), {
      method: 'DELETE',
    })
  }

  /**
   * Starts a meeting bot session.
   * Endpoint: POST `v1/meeting/start`
   */
  async startMeeting(
    meeting_url: string,
    webapp_url: string,
    agent_id?: string | null,
    workspace_id?: string | null,
    digital_coworker_id?: string | null,
    translator_mode?: boolean,
    entire_data_catalog: boolean = false
  ): Promise<unknown> {
    const requestBody: MeetingStartRequest = {
      meeting_url,
      webapp_url,
      agent_id,
      digital_coworker_id,
      workspace_id,
      translator_mode,
      // Defaulted here rather than left optional so EVERY caller states the scope.
      entire_data_catalog,
    }

    const response = await this.fetchOrError(this.getUrl('v1/meeting/start'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody),
    })
    return response.json()
  }

  /**
   * Starts an in-app (direct-mode) meeting bot session. The returned
   * `meeting_token` is used to authenticate the browser's WebRTC
   * connection to `/v1/meeting/offer` and the SSE event stream — no
   * external Recall/Attendee invite is dispatched.
   * Endpoint: POST `v1/meeting/start-direct`
   */
  async startMeetingInApp(args: {
    agentId?: string | null
    digitalCoworkerId?: string | null
    workspaceId?: string | null
    translatorMode?: boolean
    entireDataCatalog?: boolean
  } = {}): Promise<MeetingStartDirectResponse> {
    const requestBody: MeetingStartDirectRequest = {
      agent_id: args.agentId ?? null,
      digital_coworker_id: args.digitalCoworkerId ?? null,
      workspace_id: args.workspaceId ?? null,
      translator_mode: args.translatorMode,
      // Always stated, defaulting to board scope — see MeetingStartRequest.
      entire_data_catalog: args.entireDataCatalog ?? false,
    }
    const response = await this.fetchOrError(
      this.getUrl('v1/meeting/start-direct'),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      }
    )
    return response.json()
  }

  /**
   * Best-effort stop of an in-app meeting bot session. The backend
   * makes `/v1/meeting/{token}/stop` idempotent for direct-mode
   * tokens so it's safe to call multiple times / on cleanup paths.
   * Endpoint: POST `v1/meeting/{token}/stop`
   */
  async stopMeeting(meetingToken: string): Promise<void> {
    await this.fetchOrError(
      this.getUrl(`v1/meeting/${meetingToken}/stop`),
      { method: 'POST' }
    )
  }

  // ==================== MEETING INTEGRATIONS API ====================

  /**
   * Retrieves the user's meeting integrations status (Zoom + Google Meet).
   * Endpoint: GET /meeting-integrations/status
   */
  async getMeetingIntegrationsStatus(): Promise<MeetingIntegrationsStatusResponse> {
    const response = await this.fetchOrError(
      this.getUrl('meeting-integrations/status')
    )
    return response.json()
  }

  // ==================== ZOOM API ====================

  /**
   * Exchanges a Zoom authorization code for stored credentials.
   * Endpoint: POST /meeting-integrations/zoom/link
   */
  async linkZoomAccount(
    request: ZoomLinkAccountRequest
  ): Promise<ZoomIntegrationStatusDto> {
    const response = await this.fetchOrError(
      this.getUrl('meeting-integrations/zoom/link'),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
      }
    )

    return response.json()
  }

  /**
   * Retrieves the user's Zoom integration status.
   * Endpoint: GET /meeting-integrations/zoom/status
   */
  async getZoomStatus(): Promise<ZoomIntegrationStatusDto> {
    const response = await this.fetchOrError(
      this.getUrl('meeting-integrations/zoom/status')
    )
    return response.json()
  }

  /**
   * Starts a Zoom meeting via the backend integration.
   * Endpoint: POST /meeting-integrations/zoom/start
   */
  async startZoomMeeting(
    request?: StartZoomMeetingRequest
  ): Promise<ZoomHostMeetingResponse> {
    const response = await this.fetchOrError(
      this.getUrl('meeting-integrations/zoom/start'),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request ?? {}),
      }
    )

    return response.json()
  }

  /**
   * Exchanges a Google authorization code for stored credentials.
   * Endpoint: POST /meeting-integrations/google-meet/link
   */
  async linkGoogleMeetAccount(
    request: GoogleMeetLinkAccountRequest
  ): Promise<GoogleMeetIntegrationStatusDto> {
    const response = await this.fetchOrError(
      this.getUrl('meeting-integrations/google-meet/link'),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
      }
    )

    return response.json()
  }

  /**
   * Retrieves the user's Google Meet integration status.
   * Endpoint: GET /meeting-integrations/google-meet/status
   */
  async getGoogleMeetStatus(): Promise<GoogleMeetIntegrationStatusDto> {
    const response = await this.fetchOrError(
      this.getUrl('meeting-integrations/google-meet/status')
    )
    return response.json()
  }

  /**
   * Starts a Google Meet meeting via the backend integration.
   * Endpoint: POST /meeting-integrations/google-meet/start
   */
  async startGoogleMeetMeeting(
    request?: StartGoogleMeetMeetingRequest
  ): Promise<GoogleMeetHostMeetingResponse> {
    const response = await this.fetchOrError(
      this.getUrl('meeting-integrations/google-meet/start'),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request ?? {}),
      }
    )

    return response.json()
  }

  // ==================== MS TEAMS API ====================

  /**
   * Exchanges a MS Teams authorization code for stored credentials.
   * Endpoint: POST /meeting-integrations/ms-teams/link
   */
  async linkMSTeamsAccount(
    request: MSTeamsLinkAccountRequest
  ): Promise<MSTeamsIntegrationStatusDto> {
    const response = await this.fetchOrError(
      this.getUrl('meeting-integrations/ms-teams/link'),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
      }
    )

    return response.json()
  }

  /**
   * Retrieves the user's MS Teams integration status.
   * Endpoint: GET /meeting-integrations/ms-teams/status
   */
  async getMSTeamsStatus(): Promise<MSTeamsIntegrationStatusDto> {
    const response = await this.fetchOrError(
      this.getUrl('meeting-integrations/ms-teams/status')
    )
    return response.json()
  }

  /**
   * Starts a MS Teams meeting via the backend integration.
   * Endpoint: POST /meeting-integrations/ms-teams/start
   */
  async startMSTeamsMeeting(
    request?: StartMSTeamsMeetingRequest
  ): Promise<MSTeamsHostMeetingResponse> {
    const response = await this.fetchOrError(
      this.getUrl('meeting-integrations/ms-teams/start'),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request ?? {}),
      }
    )

    return response.json()
  }


  // ==================== ADMIN INTERACTION DATA API ====================

  /**
   * Gets complete admin data including stats, conversations, and finetuning data.
   * Endpoint: GET /v1/admin/interaction-data
   */
  async getAdminInteractionData(
    conversationLimit: number = 10,
    finetuningLimit: number = 20
  ): Promise<AdminDataResponse> {
    const response = await this.fetchOrError(
      this.getUrl('v1/admin/interaction-data', {
        conversation_limit: conversationLimit,
        finetuning_limit: finetuningLimit,
      })
    )
    return response.json()
  }

  /**
   * Gets statistics about stored user interaction data.
   * Endpoint: GET /v1/admin/interaction-data/stats
   */
  async getAdminStats(): Promise<DatabaseStats> {
    const response = await this.fetchOrError(
      this.getUrl('v1/admin/interaction-data/stats')
    )
    return response.json()
  }

  /**
   * Gets recent conversations with messages.
   * Endpoint: GET /v1/admin/interaction-data/conversations
   */
  async getAdminConversations(
    limit: number = 20,
    offset: number = 0,
    withFeedbackOnly: boolean = false
  ): Promise<ConversationSummary[]> {
    const response = await this.fetchOrError(
      this.getUrl('v1/admin/interaction-data/conversations', {
        limit,
        offset,
        with_feedback_only: withFeedbackOnly,
      })
    )
    return response.json()
  }

  /**
   * Gets user-assistant message pairs for finetuning.
   * Endpoint: GET /v1/admin/interaction-data/finetuning
   */
  async getAdminFinetuningData(
    limit: number = 50,
    feedbackFilter?: 'positive' | 'negative' | 'all'
  ): Promise<FinetuningDataPoint[]> {
    const response = await this.fetchOrError(
      this.getUrl('v1/admin/interaction-data/finetuning', {
        limit,
        feedback_filter: feedbackFilter,
      })
    )
    return response.json()
  }

  /**
   * Gets context memory usage statistics.
   * Endpoint: GET /v1/admin/interaction-data/context-memory
   */
  async getAdminContextMemoryStats(): Promise<ContextMemoryStats> {
    const response = await this.fetchOrError(
      this.getUrl('v1/admin/interaction-data/context-memory')
    )
    return response.json()
  }

  // Agent Personas API Methods

  /**
   * Gets all agent personas configuration.
   * Endpoint: GET /config/agent-personas
   */
  async getAgentPersonas(): Promise<AgentPersonasConfigResponse> {
    const response = await this.fetchOrError(
      this.getUrl('config/agent-personas')
    )
    return response.json()
  }

  /**
   * Gets a specific agent persona by ID.
   * Endpoint: GET /config/agent-personas/{agent_id}
   */
  async getAgentPersona(agentId: string): Promise<AgentPersona> {
    const response = await this.fetchOrError(
      this.getUrl(`config/agent-personas/${agentId}`)
    )
    return response.json()
  }

  // Comment API Methods

  /**
   * Creates a new comment in a workspace.
   * Endpoint: POST /v1/workspaces/{workspace_id}/comments
   */
  async createComment(
    workspaceId: string,
    data: CreateCommentRequest
  ): Promise<Comment> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/workspaces/${workspaceId}/comments`),
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      }
    )
    return response.json()
  }

  /**
   * Lists comments for a workspace with optional filters.
   * Endpoint: GET /v1/workspaces/{workspace_id}/comments
   */
  async listComments(
    workspaceId: string,
    filters?: CommentFilters
  ): Promise<Comment[]> {
    const params = new URLSearchParams()
    if (filters) {
      // Only add parent_comment_id if it has a value (not null/undefined)
      if (filters.parent_comment_id) {
        params.append('parent_comment_id', filters.parent_comment_id)
      }
      if (filters.include_deleted !== undefined) {
        params.append('include_deleted', String(filters.include_deleted))
      }
      if (filters.include_resolved !== undefined) {
        params.append('include_resolved', String(filters.include_resolved))
      }
      if (filters.user_id) {
        params.append('user_id', filters.user_id)
      }
    }

    const url = params.toString()
      ? `v1/workspaces/${workspaceId}/comments?${params}`
      : `v1/workspaces/${workspaceId}/comments`

    const response = await this.fetchOrError(this.getUrl(url))
    return response.json()
  }

  /**
   * Gets a single comment by ID.
   * Endpoint: GET /v1/comments/{comment_id}
   */
  async getComment(commentId: string): Promise<Comment> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/comments/${commentId}`)
    )
    return response.json()
  }

  /**
   * Gets a comment with all its nested replies.
   * Endpoint: GET /v1/comments/{comment_id}/thread
   */
  async getCommentThread(commentId: string, signal?: AbortSignal): Promise<Comment> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/comments/${commentId}/thread`),
      { signal }
    )
    return response.json()
  }

  /**
   * Updates an existing comment.
   * Endpoint: PUT /v1/comments/{comment_id}
   */
  async updateComment(
    commentId: string,
    data: UpdateCommentRequest
  ): Promise<Comment> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/comments/${commentId}`),
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      }
    )
    return response.json()
  }

  /**
   * Deletes a comment (soft delete).
   * Endpoint: DELETE /v1/comments/{comment_id}
   */
  async deleteComment(
    commentId: string
  ): Promise<{ status: string; comment_id: string }> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/comments/${commentId}`),
      {
        method: 'DELETE',
      }
    )
    return response.json()
  }

  /**
   * Marks a comment as resolved or unresolved.
   * Endpoint: POST /v1/comments/{comment_id}/resolve
   */
  async resolveComment(
    commentId: string,
    isResolved: boolean
  ): Promise<Comment> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/comments/${commentId}/resolve`),
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ is_resolved: isResolved }),
      }
    )
    return response.json()
  }

  async addReaction(commentId: string, reactionType: string): Promise<any> {
    const response = await this.fetchOrError(
      this.getUrl(
        `v1/comments/${commentId}/reactions/${encodeURIComponent(reactionType)}`
      ),
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    )
    return response.json()
  }

  async removeReaction(commentId: string, reactionType: string): Promise<void> {
    await this.fetchOrError(
      this.getUrl(
        `v1/comments/${commentId}/reactions/${encodeURIComponent(reactionType)}`
      ),
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    )
  }

  async getReactions(tenantId: string, commentId: string): Promise<any[]> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/${tenantId}/comments/${commentId}/reactions`),
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    )
    return response.json()
  }

  // Visual Elements API Methods
  async getDocumentVisualElements(
    documentId: string
  ): Promise<DocumentVisualElements> {
    const path = `v1/visual-elements/document/${documentId}`
    const response = await this.fetchOrError(this.getUrl(path), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })
    return response.json()
  }

  async getPageVisualElements(
    documentId: string,
    pageNumber: number
  ): Promise<VisualElementSummary[]> {
    const response = await this.fetchOrError(
      this.getUrl(
        `v1/visual-elements/document/${documentId}/page/${pageNumber}`
      ),
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    )
    return response.json()
  }

  async getVisualElementDetails(elementId: string): Promise<VisualElement> {
    const path = `v1/visual-elements/element/${elementId}`
    const response = await this.fetchOrError(this.getUrl(path), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })
    return response.json()
  }

  getVisualElementImageUrl(
    elementId: string,
    thumbnail: boolean = false
  ): string {
    const params = thumbnail ? '?thumbnail=true' : ''
    return this.getUrl(`v1/visual-elements/element/${elementId}/image${params}`)
  }

  // ========================================================================
  // Extracted Tables API
  // ========================================================================

  /**
   * Get structured tables extracted from a document during indexing.
   * Tables include rows, columns, headers, and confidence scores.
   */
  async getDocumentExtractedTables(
    documentId: string
  ): Promise<ExtractedTablesResponse> {
    const path = `v1/index/${documentId}/tables`
    const response = await this.fetchOrError(this.getUrl(path), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })
    return response.json()
  }

  // ========================================================================
  // Mixed Documents API (P&ID + Text Pages)
  // ========================================================================

  /**
   * Get all page classifications for a document.
   * Endpoint: GET /v1/mixed-documents/{documentId}/page-classifications
   */
  async getPageClassifications(
    documentId: string
  ): Promise<import('../types/mixed-documents').PageClassification[]> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/mixed-documents/${documentId}/page-classifications`)
    )
    return response.json()
  }

  /**
   * Get statistics about page classifications for a document.
   * Endpoint: GET /v1/mixed-documents/{documentId}/statistics
   */
  async getDocumentStatistics(
    documentId: string
  ): Promise<import('../types/mixed-documents').DocumentStatistics> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/mixed-documents/${documentId}/statistics`)
    )
    return response.json()
  }

  /**
   * Get all pages classified as P&ID for a document.
   * Endpoint: GET /v1/mixed-documents/{documentId}/pnid-pages
   */
  async getPnidPages(
    documentId: string
  ): Promise<import('../types/mixed-documents').PageClassification[]> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/mixed-documents/${documentId}/pnid-pages`)
    )
    return response.json()
  }

  /**
   * Get all pages classified as text/other for a document.
   * Endpoint: GET /v1/mixed-documents/{documentId}/text-pages
   */
  async getTextPages(
    documentId: string
  ): Promise<import('../types/mixed-documents').PageClassification[]> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/mixed-documents/${documentId}/text-pages`)
    )
    return response.json()
  }

  /**
   * Get classification for a specific page in a document.
   * Endpoint: GET /v1/mixed-documents/{documentId}/page/{pageNumber}
   */
  async getPageClassification(
    documentId: string,
    pageNumber: number
  ): Promise<import('../types/mixed-documents').PageClassification> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/mixed-documents/${documentId}/page/${pageNumber}`)
    )
    return response.json()
  }

  /**
   * Update classification for a specific page (manual override).
   * This triggers P&ID workflow if marking a page as P&ID.
   * Endpoint: PATCH /v1/mixed-documents/{documentId}/page-classifications/{pageNumber}
   */
  async updatePageClassification(
    documentId: string,
    pageNumber: number,
    pageType: 'pnid' | 'other',
    confidence: number = 1.0
  ): Promise<
    import('../types/mixed-documents').UpdatePageClassificationResponse
  > {
    const response = await this.fetchOrError(
      this.getUrl(
        `v1/mixed-documents/${documentId}/page-classifications/${pageNumber}`
      ),
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          page_type: pageType,
          confidence: confidence,
          detection_method: 'manual_override',
        }),
      }
    )
    return response.json()
  }
  // ==================== ONBOARDING API ====================

  /**
   * Gets all onboarding questions for the tenant.
   * Includes tenant-specific questions and global default questions.
   * Endpoint: GET /v1/onboarding/questions
   */
  async getOnboardingQuestions(): Promise<OnboardingQuestion[]> {
    const response = await this.fetchOrError(
      this.getUrl('v1/onboarding/questions')
    )
    return response.json()
  }

  /**
   * Gets all questions for admin management (including inactive).
   * Endpoint: GET /v1/onboarding/questions/admin
   */
  async getOnboardingQuestionsAdmin(): Promise<OnboardingQuestion[]> {
    const response = await this.fetchOrError(
      this.getUrl('v1/onboarding/questions/admin')
    )
    return response.json()
  }

  /**
   * Creates a new onboarding question (Admin only).
   * Endpoint: POST /v1/onboarding/questions
   */
  async createOnboardingQuestion(
    request: CreateOnboardingQuestionRequest
  ): Promise<OnboardingQuestion> {
    const response = await this.fetchOrError(
      this.getUrl('v1/onboarding/questions'),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
      }
    )
    return response.json()
  }

  /**
   * Updates an onboarding question (Admin only).
   * Endpoint: PUT /v1/onboarding/questions/{id}
   */
  async updateOnboardingQuestion(
    questionId: string,
    request: UpdateOnboardingQuestionRequest
  ): Promise<OnboardingQuestion> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/onboarding/questions/${questionId}`),
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
      }
    )
    return response.json()
  }

  /**
   * Deletes an onboarding question (Admin only, soft delete).
   * Endpoint: DELETE /v1/onboarding/questions/{id}
   */
  async deleteOnboardingQuestion(questionId: string): Promise<void> {
    await this.fetchOrError(
      this.getUrl(`v1/onboarding/questions/${questionId}`),
      { method: 'DELETE' }
    )
  }

  /**
   * Gets the current user's onboarding answers.
   * Endpoint: GET /v1/onboarding/answers
   */
  async getMyOnboardingAnswers(): Promise<OnboardingUserAnswersResponse> {
    const response = await this.fetchOrError(
      this.getUrl('v1/onboarding/answers')
    )
    return response.json()
  }

  /**
   * Submits or updates onboarding answers for the current user.
   * Endpoint: POST /v1/onboarding/answers
   */
  async submitOnboardingAnswers(
    request: SubmitOnboardingAnswersRequest
  ): Promise<OnboardingUserAnswersResponse> {
    const response = await this.fetchOrError(
      this.getUrl('v1/onboarding/answers'),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
      }
    )
    return response.json()
  }

  /**
   * Gets onboarding completion status for the current user.
   * Endpoint: GET /v1/onboarding/status
   */
  async getOnboardingStatus(): Promise<{ completed: boolean }> {
    const response = await this.fetchOrError(
      this.getUrl('v1/onboarding/status')
    )
    return response.json()
  }

  /**
   * Records that the current user has finished first-time onboarding. Durable,
   * server-side signal so onboarding is not re-shown after a cache/localStorage
   * clear or on a new device . Idempotent.
   * Endpoint: POST /v1/onboarding/complete
   */
  async completeOnboarding(): Promise<{ completed: boolean }> {
    const response = await this.fetchOrError(this.getUrl('v1/onboarding/complete'), {
      method: 'POST',
    })
    return response.json()
  }

  /**
   * Whether the current user has finished first-time onboarding (durable). Read
   * by the route guard to hydrate its localStorage fast-path cache .
   * Endpoint: GET /v1/onboarding/completed
   */
  /**
   * the agent this user's chat should open on, derived from their
   * onboarding answers. `agent_id: null` = keep the app's existing default.
   * Advisory only — it never gates which agents are selectable.
   * Endpoint: GET /v1/onboarding/preferred-agent
   */
  async getPreferredOnboardingAgent(): Promise<{ agent_id: string | null }> {
    const response = await this.fetchOrError(this.getUrl('v1/onboarding/preferred-agent'))
    return response.json()
  }

  /**
   * Product areas this person may use (per-person PostHog flags, enforced by the backend).
   * Endpoint: GET /v2/me/features
   */
  async getMyFeatures(): Promise<{ features: string[] }> {
    const response = await this.fetchOrError(this.getUrl('v2/me/features'))
    return response.json()
  }

  async getOnboardingCompleted(): Promise<{ completed: boolean }> {
    const response = await this.fetchOrError(
      this.getUrl('v1/onboarding/completed')
    )
    return response.json()
  }

  /**
   * Gets all users' onboarding answers (Admin only).
   * Endpoint: GET /v1/onboarding/answers/all
   */
  async getAllUsersOnboardingAnswers(): Promise<OnboardingAllUsersAnswersResponse> {
    const response = await this.fetchOrError(
      this.getUrl('v1/onboarding/answers/all')
    )
    return response.json()
  }

  // ==================== DIGITAL COWORKERS API ====================

  /**
   * List all digital coworkers for the tenant.
   * Endpoint: GET /v1/digital-coworkers
   */
  async listDigitalCoworkers(params?: {
    status?: string
    search?: string
  }, options?: { signal?: AbortSignal }): Promise<DigitalCoworkersListResponse> {
    const query = new URLSearchParams()
    if (params?.status) query.set('status', params.status)
    if (params?.search) query.set('search', params.search)
    const qs = query.toString() ? `?${query.toString()}` : ''
    const response = await this.fetchOrError(
      this.getUrl(`v1/digital-coworkers${qs}`),
      options?.signal ? { signal: options.signal } : undefined
    )
    return response.json()
  }

  /**
   * Create a new digital coworker (multipart/form-data for photo upload).
   * Endpoint: POST /v1/digital-coworkers
   */
  async createDigitalCoworker(
    data: CreateDigitalCoworkerRequest,
    photo?: File
  ): Promise<DigitalCoworkerSingleResponse> {
    const formData = new FormData()
    formData.append('name', data.name)
    formData.append('job_title', data.job_title)
    formData.append('department', data.department)
    formData.append('type', data.type)
    if (data.expertise) formData.append('expertise', data.expertise)
    if (photo) formData.append('photo', photo)

    const response = await this.fetchOrError(
      this.getUrl('v1/digital-coworkers'),
      { method: 'POST', body: formData }
    )
    return response.json()
  }

  /**
   * Update a digital coworker.
   * Endpoint: PUT /v1/digital-coworkers/:id
   */
  async updateDigitalCoworker(
    id: string,
    data: Partial<CreateDigitalCoworkerRequest>,
    photo?: File
  ): Promise<DigitalCoworkerSingleResponse> {
    const formData = new FormData()
    if (data.name) formData.append('name', data.name)
    if (data.job_title) formData.append('job_title', data.job_title)
    if (data.department) formData.append('department', data.department)
    if (data.type) formData.append('type', data.type)
    if (data.expertise !== undefined) formData.append('expertise', data.expertise ?? '')
    if (photo) formData.append('photo', photo)

    const response = await this.fetchOrError(
      this.getUrl(`v1/digital-coworkers/${id}`),
      { method: 'PUT', body: formData }
    )
    return response.json()
  }

  /**
   * Delete a digital coworker.
   * Endpoint: DELETE /v1/digital-coworkers/:id
   */
  async deleteDigitalCoworker(id: string): Promise<{ success: boolean }> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/digital-coworkers/${id}`),
      { method: 'DELETE' }
    )
    return response.json()
  }

  /**
   * Link Google account to a digital coworker.
   * Endpoint: POST /v1/digital-coworkers/:id/integrations/google/link
   */
  async linkDigitalCoworkerGoogleAccount(
    id: string,
    code: string,
    redirectUri: string
  ): Promise<{ success: boolean; email: string }> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/digital-coworkers/${id}/integrations/google/link`),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, redirect_uri: redirectUri }),
      }
    )
    return response.json()
  }

  /**
   * Trigger manual sync of Google data for a digital coworker.
   * Endpoint: POST /v1/digital-coworkers/:id/integrations/google/sync
   */
  async syncDigitalCoworkerGoogleData(
    id: string
  ): Promise<{ success: boolean; synced_at: string }> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/digital-coworkers/${id}/integrations/google/sync`),
      { method: 'POST' }
    )
    return response.json()
  }

  /**
   * Unlink Google account from a digital coworker.
   * Endpoint: DELETE /v1/digital-coworkers/:id/integrations/google/unlink
   */
  async unlinkDigitalCoworkerGoogleAccount(
    id: string
  ): Promise<{ success: boolean }> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/digital-coworkers/${id}/integrations/google/unlink`),
      { method: 'DELETE' }
    )
    return response.json()
  }

  /**
   * Link Microsoft (OneDrive) account to a digital coworker.
   * Endpoint: POST /v1/digital-coworkers/:id/integrations/microsoft/link
   */
  async linkDigitalCoworkerMicrosoftAccount(
    id: string,
    code: string,
    redirectUri: string
  ): Promise<{ success: boolean; email: string }> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/digital-coworkers/${id}/integrations/microsoft/link`),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, redirect_uri: redirectUri }),
      }
    )
    return response.json()
  }

  /**
   * Trigger manual sync of Microsoft OneDrive data for a digital coworker.
   * Endpoint: POST /v1/digital-coworkers/:id/integrations/microsoft/sync
   */
  async syncDigitalCoworkerOneDriveData(
    id: string
  ): Promise<{ success: boolean; synced_at: string }> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/digital-coworkers/${id}/integrations/microsoft/sync`),
      { method: 'POST' }
    )
    return response.json()
  }

  /**
   * Unlink Microsoft (OneDrive) account from a digital coworker.
   * Endpoint: DELETE /v1/digital-coworkers/:id/integrations/microsoft/unlink
   */
  async unlinkDigitalCoworkerMicrosoftAccount(
    id: string
  ): Promise<{ success: boolean }> {
    const response = await this.fetchOrError(
      this.getUrl(`v1/digital-coworkers/${id}/integrations/microsoft/unlink`),
      { method: 'DELETE' }
    )
    return response.json()
  }

  /**
   * Send an email invitation to a colleague to create their Digital Coworker.
   * Endpoint: POST /v1/digital-coworkers/invite-colleague
   */
  async inviteColleague(params: {
    colleague_email: string
    webapp_origin: string
  }): Promise<{ success: boolean }> {
    const response = await this.fetchOrError(
      this.getUrl('v1/digital-coworkers/invite-colleague'),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params),
      }
    )
    return response.json()
  }

  /**
   * Send a message to a digital coworker and stream the SSE response.
   * Endpoint: POST /v1/digital-coworkers/:id/chat
   * Returns the raw Response so the caller can read the SSE stream.
   */
  async chatWithDigitalCoworker(id: string, message: string, signal?: AbortSignal): Promise<Response> {
    return this.fetchOrError(
      this.getUrl(`v1/digital-coworkers/${id}/chat`),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message }),
        signal,
      },
      // SSE: the default request deadline would also cut the stream off mid-read.
      { timeoutMs: null }
    )
  }

  // ==================== FEEDBACK / BUG REPORT ====================

  /**
   * Submit user feedback/bug report to Linear.
   * Endpoint: POST /v1/feedback
   */
  async submitFeedback(params: {
    name?: string
    email?: string
    message: string
    url?: string
    userAgent?: string
    correlationIds?: string
    stackTrace?: string
    /** IANA zone from the reporter's browser, e.g. `Asia/Riyadh` (ENG-2936). */
    timezone?: string
    attachments?: File[]
  }): Promise<{
    success: boolean
    issueId?: string
    issueIdentifier?: string
    issueUrl?: string
    error?: string
  }> {
    const formData = new FormData()

    if (params.name) {
      formData.append('name', params.name)
    }
    if (params.email) {
      formData.append('email', params.email)
    }
    formData.append('message', params.message)
    if (params.url) {
      formData.append('url', params.url)
    }
    if (params.userAgent) {
      formData.append('userAgent', params.userAgent)
    }
    if (params.correlationIds) {
      formData.append('correlationIds', params.correlationIds)
    }
    if (params.stackTrace) {
      formData.append('stackTrace', params.stackTrace)
    }
    if (params.timezone) {
      formData.append('timezone', params.timezone)
    }

    // Append attachments
    if (params.attachments) {
      for (const file of params.attachments) {
        formData.append('attachments', file)
      }
    }

    const response = await this.fetchOrError(this.getUrl('v1/feedback'), {
      method: 'POST',
      body: formData,
    })

    return response.json()
  }

  // ─── Unified Knowledge Graph (UKG) ────────────────────────────────────────

  async getTenantEntitlements(): Promise<TenantEntitlementsResponse> {
    const response = await this.fetchOrError(this.getUrl('v1/organization/entitlements'))
    return response.json()
  }

  async getUkgStatus(): Promise<UkgStatusResponse> {
    const response = await this.fetchOrError(this.getUrl('v2/ukg/status'))
    return response.json()
  }

  async getUkgHierarchy(): Promise<UkgHierarchyResponse> {
    const response = await this.fetchOrError(this.getUrl('v2/ukg/hierarchy'))
    return response.json()
  }

  /**
   * GET v2/ukg/agent-graph — the KG the native agent answers from (the tenant
   * workspace's `raw/*.parquet`), as sheets, equipment and connections. Behind
   * the Knowledge Graph page's internal "Agent KG" switch; nothing else reads it.
   */
  async getUkgAgentGraph(): Promise<UkgAgentGraphResponse> {
    const response = await this.fetchOrError(this.getUrl('v2/ukg/agent-graph'))
    return response.json()
  }
}

/** GET v1/organization/entitlements — the active tenant's product layers (Opening Cortex). */
export type TenantEntitlementLayer = 'retina' | 'ukg'
export interface TenantEntitlementsResponse {
  tenant_id: string
  layers: TenantEntitlementLayer[]
  source: 'default' | 'record'
}

export interface UkgStatusResponse {
  tenant_id: string
  ready: boolean
  message?: string
}

export interface UkgHierarchyDocument {
  id: string | null
  name: string | null
  type: string | null
  status: string | null
  drawing_number: string | null
  // Other documents this one is cross-sheet connected to (off-page connectors).
  // Drives doc→doc edges in the KG view. Optional for backward compat with
  // older backends that don't emit it.
  connected_document_ids?: string[]
  /** Uploader's Keycloak id and display name (DSGN-63 "Uploaded by" filter).
   *  Optional for backends that predate them. */
  user_id?: string | null
  uploader_name?: string | null
}

export interface UkgHierarchySite {
  site_id: string
  name: string
  document_count: number
  documents: UkgHierarchyDocument[]
}

export interface UkgHierarchyResponse {
  tenant_id: string
  sites: UkgHierarchySite[]
}

/** One parsed P&ID sheet in `raw/sheet_meta` (`GET v2/ukg/agent-graph`). */
export interface UkgAgentGraphSheet {
  sheet: string
  /** The document the sheet was parsed from; null when regen recorded none. */
  document_id: string | null
  /** That document's uploader and their display name (DSGN-63 "Uploaded by"); null when unknown. */
  uploader_id: string | null
  uploader_name: string | null
  sheet_number: string | null
  drawing_number: string | null
  drawing_type: string | null
  system: string | null
  area_of_plant: string | null
  symbol_count: number
}

/** One canonical equipment in `raw/equipment_meta`, with the sheets it appears on (home first). */
export interface UkgAgentGraphEquipment {
  tag: string
  name: string | null
  equipment_type: string | null
  sheets: string[]
  home_sheet: string | null
}

/** One walked tag→tag adjacency from `raw/connectivity`, one per (src, dst). */
export interface UkgAgentGraphConnection {
  src_tag: string
  dst_tag: string
  sheet: string | null
  line: string | null
  direction: string | null
  hop_count: number | null
}

export interface UkgAgentGraphResponse {
  tenant_id: string
  workspace: string | null
  /** False when this pod holds no `raw/` for the tenant; `reason` says why and the lists are empty. */
  available: boolean
  reason?: string
  sheets: UkgAgentGraphSheet[]
  equipment: UkgAgentGraphEquipment[]
  connections: UkgAgentGraphConnection[]
  /** A per-table cap cut rows: the graph is a sample. */
  truncated: boolean
}

export const apiService = new ApiService(config.apiBaseUrl)
